import {
  StrictMode,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createRoot } from "react-dom/client";
import "pretendard/dist/web/variable/pretendardvariable-dynamic-subset.css";
import {
  api,
  setCsrfToken,
  type Flags,
  type CatalogModel,
  type CatalogPreset,
  type CreditSummary,
  type DescriptionApplicationOptions,
  type Job,
  type OutfitCandidate,
  type Product,
  type ProductCategory,
  type ProductDashboardSummary,
  type BillingPolicy,
  type Cafe24CreditPackage,
  type RepresentativePreview,
  type Session,
  type Upload,
} from "./api";
import {
  reusableReshootSource,
  type ReshootSource,
} from "./reshoot";
import {
  groupPresetsByFraming,
  sortPresetsForStudio,
  STUDIO_FRAMING_LABELS,
  STUDIO_FRAMING_ORDER,
} from "./presetSort";
import {
  commercePlatform,
  commerceProductUrl,
  platformUi,
} from "./platform";
import { loadSharedFooter } from "./sharedFooter";
import "./styles.css";
import "./landing.css";
import "./saas-polish.css";

const FALLBACK_MODELS: CatalogModel[] = [
  {
    id: "female-seoa",
    name: "서아",
    gender: "여성 · 한국",
    genderCode: "female",
    detail: "정제된 뷰티 카탈로그",
    image: "/models/female-seoa.jpg",
  },
  {
    id: "female-jiyu",
    name: "지유",
    gender: "여성 · 한국",
    genderCode: "female",
    detail: "모던하고 자연스러운 무드",
    image: "/models/female-jiyu.jpg",
  },
  {
    id: "female-rin",
    name: "린",
    gender: "여성 · 한국",
    genderCode: "female",
    detail: "강렬한 하이패션 무드",
    image: "/models/female-rin.jpg",
  },
  {
    id: "female-elena",
    name: "엘레나",
    gender: "여성 · 유럽",
    genderCode: "female",
    detail: "북유럽 미니멀 럭셔리",
    image: "/models/female-elena.jpg",
  },
  {
    id: "female-camille",
    name: "카미유",
    gender: "여성 · 유럽",
    genderCode: "female",
    detail: "프렌치 쿠튀르 무드",
    image: "/models/female-camille.jpg",
  },
  {
    id: "female-sofia",
    name: "소피아",
    gender: "여성 · 유럽",
    genderCode: "female",
    detail: "밀라노 런웨이 무드",
    image: "/models/female-sofia.jpg",
  },
  {
    id: "male-dojin",
    name: "도진",
    gender: "남성 · 한국",
    genderCode: "male",
    detail: "클래식 럭셔리 무드",
    image: "/models/male-dojin.jpg",
  },
  {
    id: "male-hyeon",
    name: "현",
    gender: "남성 · 한국",
    genderCode: "male",
    detail: "건강하고 편안한 무드",
    image: "/models/male-hyeon.jpg",
  },
  {
    id: "male-ian",
    name: "이안",
    gender: "남성 · 한국",
    genderCode: "male",
    detail: "도회적인 에디토리얼",
    image: "/models/male-ian.jpg",
  },
  {
    id: "male-lukas",
    name: "루카스",
    gender: "남성 · 유럽",
    genderCode: "male",
    detail: "북유럽 모던 클래식",
    image: "/models/male-lukas.jpg",
  },
  {
    id: "male-theo",
    name: "테오",
    gender: "남성 · 유럽",
    genderCode: "male",
    detail: "파리지앵 럭셔리 무드",
    image: "/models/male-theo.jpg",
  },
  {
    id: "male-marco",
    name: "마르코",
    gender: "남성 · 유럽",
    genderCode: "male",
    detail: "밀라노 클래식 무드",
    image: "/models/male-marco.jpg",
  },
  {
    id: "female-mira",
    name: "미라",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-mira.jpg",
  },
  {
    id: "female-nika",
    name: "니카",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-nika.jpg",
  },
  {
    id: "female-elise",
    name: "엘리즈",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-elise.jpg",
  },
  {
    id: "female-amara",
    name: "아마라",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-amara.jpg",
  },
  {
    id: "female-ines",
    name: "이네스",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-ines.jpg",
  },
  {
    id: "male-adrien",
    name: "아드리안",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-adrien.jpg",
  },
  {
    id: "male-rafael",
    name: "라파엘",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-rafael.jpg",
  },
  {
    id: "male-cael",
    name: "카엘",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-cael.jpg",
  },
  {
    id: "male-ren",
    name: "렌",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-ren.jpg",
  },
  {
    id: "male-finn",
    name: "핀",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-finn.jpg",
  },
  {
    id: "female-livia",
    name: "리비아",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-livia.jpg",
  },
  {
    id: "male-soren",
    name: "소렌",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-soren.jpg",
  },
  {
    id: "female-noa",
    name: "노아",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-noa.jpg",
  },
  {
    id: "male-dario",
    name: "다리오",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-dario.jpg",
  },
  {
    id: "female-roxane",
    name: "록산",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-roxane.jpg",
  },
  {
    id: "male-evan",
    name: "에반",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-evan.jpg",
  },
  {
    id: "male-julien",
    name: "줄리앙",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-julien.jpg",
  },
  {
    id: "female-maelle",
    name: "마엘",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-maelle.jpg",
  },
  {
    id: "female-clara",
    name: "클라라",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-clara.jpg",
  },
  {
    id: "male-isaac",
    name: "아이작",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-isaac.jpg",
  },
  {
    id: "female-salome",
    name: "살로메",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-salome.jpg",
  },
  {
    id: "male-lucien",
    name: "루시앙",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-lucien.jpg",
  },
  {
    id: "male-felix",
    name: "펠릭스",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-felix.jpg",
  },
  {
    id: "female-vera",
    name: "베라",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-vera.jpg",
  },
  {
    id: "male-malek",
    name: "말렉",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-malek.jpg",
  },
  {
    id: "female-tala",
    name: "탈라",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-tala.jpg",
  },
  {
    id: "male-kenzo",
    name: "켄조",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-kenzo.jpg",
  },
  {
    id: "male-andre",
    name: "안드레",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-andre.jpg",
  },
  {
    id: "female-yuna",
    name: "유나",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-yuna.jpg",
  },
  {
    id: "female-alina",
    name: "알리나",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-alina.jpg",
  },
  {
    id: "male-seojun",
    name: "서준",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-seojun.jpg",
  },
  {
    id: "female-daphne",
    name: "다프네",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-daphne.jpg",
  },
  {
    id: "female-irina",
    name: "이리나",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-irina.jpg",
  },
  {
    id: "female-maeve",
    name: "메이브",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-maeve.jpg",
  },
  {
    id: "male-haru",
    name: "하루",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-haru.jpg",
  },
  {
    id: "female-zuri",
    name: "주리",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-zuri.jpg",
  },
  {
    id: "male-emil",
    name: "에밀",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-emil.jpg",
  },
  {
    id: "male-gabriel",
    name: "가브리엘",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-gabriel.jpg",
  },
  {
    id: "female-celine",
    name: "셀린",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-celine.jpg",
  },
  {
    id: "male-niels",
    name: "닐스",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-niels.jpg",
  },
  {
    id: "female-freya",
    name: "프레야",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-freya.jpg",
  },
  {
    id: "male-omar",
    name: "오마르",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-omar.jpg",
  },
  {
    id: "female-axelle",
    name: "악셀",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-axelle.jpg",
  },
  {
    id: "male-minjae",
    name: "민재",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-minjae.jpg",
  },
  {
    id: "female-imani",
    name: "이마니",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-imani.jpg",
  },
  {
    id: "female-sora",
    name: "소라",
    gender: "여성 · 글로벌",
    genderCode: "female",
    detail: "프리미엄 패션 모델",
    image: "/models/female-sora.jpg",
  },
  {
    id: "male-rowan",
    name: "로완",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-rowan.jpg",
  },
  {
    id: "male-elias",
    name: "엘리아스",
    gender: "남성 · 글로벌",
    genderCode: "male",
    detail: "프리미엄 패션 모델",
    image: "/models/male-elias.jpg",
  },
];

const FALLBACK_PRESETS: CatalogPreset[] = [
  {
    code: "studio-pose-001",
    title: "정면 스탠딩",
    subtitle: "전신 · 정면 · 스탠딩",
    image: "/preset-poses/studio-pose-001.jpg",
    framing: "full-body",
    viewAngle: "front",
    poseType: "standing",
    fitFor: ["all-fashion", "swimwear", "dress", "top"],
    compatiblePresentations: ["female", "male"],
    posePresentation: "universal",
    occlusion: ["none"],
    cameraHeight: "eye-level",
    order: 10,
  },
];

type WorkspaceView = "dashboard" | "studio" | "gallery" | "tokens" | "settings";
type StudioStep = "product" | "outfit" | "model" | "style" | "engine";
type OutfitSelection =
  | (OutfitCandidate & { uploadId?: never })
  | {
      id: string;
      source: "upload";
      url: string;
      label: string;
      confidence: "high";
      warnings: string[];
      uploadId: string;
    };
type PersonMode = "provided_model" | "personal_face";
type GarmentView = "front" | "back" | "both";
type ApplicationFeedback = {
  action: "apply" | "remove" | "layout" | "thumbnail";
  phase: "working" | "success" | "error";
  productNo: number;
  message: string;
};
type GenerationRequestFeedback = {
  phase: "submitting" | "accepted" | "error";
  message: string;
};
const DESCRIPTION_LAYOUTS: Array<{
  key: DescriptionApplicationOptions["layout"];
  name: string;
  description: string;
}> = [
  { key: "clean", name: "클린 센터", description: "본문 폭에 맞춘 가장 단정한 기본형" },
  { key: "full", name: "와이드 비주얼", description: "사용 가능한 본문 폭을 가득 채우는 이미지형" },
  { key: "editorial", name: "에디토리얼 프레임", description: "차분한 여백과 배경으로 화보를 강조" },
  { key: "magazine", name: "매거진 캡션", description: "이미지 아래 메시지를 편집물처럼 구성" },
];
type ToastItem = {
  id: string;
  tone: "info" | "working" | "success" | "error";
  title: string;
  message: string;
  expiresAt: number | null;
  actionLabel?: string;
  actionHref?: string;
  actionView?: WorkspaceView;
};
const appliedApplicationStatuses = new Set([
  "applied",
  "applied_unverified",
  "write_unknown",
]);
const isAppliedStatus = (status: string | null | undefined) =>
  Boolean(status && appliedApplicationStatuses.has(status));

type UiIconName =
  | "home"
  | "close"
  | "external"
  | "download"
  | "refresh"
  | "image"
  | "plus"
  | "align"
  | "trash"
  | "arrow-left"
  | "arrow-right"
  | "gallery"
  | "retry"
  | "search"
  | "filter"
  | "spark"
  | "history"
  | "clock"
  | "credit"
  | "settings";

function UiIcon({ name }: { name: UiIconName }) {
  const paths: Record<UiIconName, ReactNode> = {
    home: <><path d="M3.5 11.5 12 4l8.5 7.5" /><path d="M5.5 10v9.5h13V10M9.5 19.5v-6h5v6" /></>,
    close: <><path d="M5 5l14 14M19 5L5 19" /></>,
    external: <><path d="M14 4h6v6M20 4l-9 9" /><path d="M19 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h6" /></>,
    download: <><path d="M12 4v11M7.5 11.5 12 16l4.5-4.5" /><path d="M5 20h14" /></>,
    refresh: <><path d="M20 7v5h-5" /><path d="M18.5 16A8 8 0 1 1 19 8l1 4" /></>,
    image: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9" r="1.5" /><path d="M4 17l5-5 4 4 2-2 5 4" /></>,
    plus: <><path d="M12 5v14M5 12h14" /></>,
    align: <><path d="M5 6h14M8 10h8M5 14h14M8 18h8" /></>,
    trash: <><path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5" /></>,
    "arrow-left": <><path d="M19 12H5M11 6l-6 6 6 6" /></>,
    "arrow-right": <><path d="M5 12h14M13 6l6 6-6 6" /></>,
    gallery: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 15l3-3 3 3 3-4 4 5" /></>,
    retry: <><path d="M4 4v6h6" /><path d="M5.5 16a8 8 0 1 0 0-8L4 10" /></>,
    search: <><circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" /></>,
    filter: <><path d="M4 6h16M7 12h10M10 18h4" /></>,
    spark: <><path d="M12 3l1.7 5.3L19 10l-5.3 1.7L12 17l-1.7-5.3L5 10l5.3-1.7L12 3Z" /><path d="M19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8L19 16Z" /></>,
    history: <><path d="M4 5v5h5" /><path d="M5.6 16.5A8 8 0 1 0 5 9.2L4 10" /><path d="M12 7.5V12l3 2" /></>,
    clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3.2 2" /></>,
    credit: <><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M3 9h18M7 15h4" /></>,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M19 13.5v-3l-2-.6a7 7 0 0 0-.7-1.6l1-1.8-2.1-2.1-1.8 1a7 7 0 0 0-1.6-.7L11.2 3h-3l-.6 2a7 7 0 0 0-1.6.7l-1.8-1-2.1 2.1 1 1.8a7 7 0 0 0-.7 1.6l-2 .6v3l2 .6a7 7 0 0 0 .7 1.6l-1 1.8 2.1 2.1 1.8-1a7 7 0 0 0 1.6.7l.6 2h3l.6-2a7 7 0 0 0 1.6-.7l1.8 1 2.1-2.1-1-1.8a7 7 0 0 0 .7-1.6l2-.6Z" transform="translate(2.4 0) scale(.8)" /></>,
  };
  return (
    <svg className="ui-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {paths[name]}
    </svg>
  );
}

function ProgressOrb({
  progress,
  compact = false,
}: {
  progress: number;
  compact?: boolean;
}) {
  const bounded = Math.max(0, Math.min(100, Math.round(progress)));
  return (
    <span
      className={`progress-orb ${compact ? "compact" : ""}`}
      aria-label={`생성 진행률 ${bounded}%`}
    >
      <svg viewBox="0 0 120 120" aria-hidden="true">
        <circle className="progress-orb-track" cx="60" cy="60" r="50" />
        <circle
          className="progress-orb-value"
          cx="60"
          cy="60"
          r="50"
          pathLength="100"
          strokeDasharray={`${bounded} 100`}
          transform="rotate(-90 60 60)"
        />
        <circle className="progress-orb-halo" cx="60" cy="60" r="39" />
      </svg>
      <span className="progress-orb-label">
        <strong>{bounded}</strong>
        {!compact && <small>%</small>}
      </span>
    </span>
  );
}

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [flags, setFlags] = useState<Flags | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [productSummary, setProductSummary] =
    useState<ProductDashboardSummary | null>(null);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [productCategoriesLoading, setProductCategoriesLoading] = useState(true);
  const [productCategoriesUnavailable, setProductCategoriesUnavailable] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const [productCategoryNo, setProductCategoryNo] = useState<number | null>(null);
  const [productStatus, setProductStatus] = useState<
    "all" | "selling" | "hidden"
  >("all");
  const [nextProductOffset, setNextProductOffset] = useState<number | null>(
    null,
  );
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productLoadError, setProductLoadError] = useState<string | null>(null);
  const [productReloadKey, setProductReloadKey] = useState(0);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [credits, setCredits] = useState<CreditSummary | null>(null);
  const [billingPolicy, setBillingPolicy] = useState<BillingPolicy | null>(null);
  const [creditPackages, setCreditPackages] = useState<Cafe24CreditPackage[]>([]);
  const [purchasingPackageId, setPurchasingPackageId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Product | null>(null);
  const [outfitCandidates, setOutfitCandidates] = useState<OutfitCandidate[]>(
    [],
  );
  const [selectedOutfit, setSelectedOutfit] = useState<OutfitSelection | null>(
    null,
  );
  const [reshootSource, setReshootSource] = useState<ReshootSource | null>(
    null,
  );
  const [outfitConfirmed, setOutfitConfirmed] = useState(false);
  const [garmentView, setGarmentView] = useState<GarmentView>("front");
  const [loadingOutfits, setLoadingOutfits] = useState(false);
  const [uploadingOutfit, setUploadingOutfit] = useState(false);
  const [personMode, setPersonMode] = useState<PersonMode>("provided_model");
  const [personUpload, setPersonUpload] = useState<Upload | null>(null);
  const [uploadingPerson, setUploadingPerson] = useState(false);
  const [personalConsent, setPersonalConsent] = useState(false);
  const [view, setView] = useState<WorkspaceView>("dashboard");
  const [studioStep, setStudioStep] = useState<StudioStep>("product");
  const [viewerJob, setViewerJob] = useState<Job | null>(null);
  const [representativeJob, setRepresentativeJob] = useState<Job | null>(null);
  const [representativePreview, setRepresentativePreview] =
    useState<RepresentativePreview | null>(null);
  const [representativeMode, setRepresentativeMode] = useState<
    "contain" | "cover"
  >("contain");
  const [representativeBusy, setRepresentativeBusy] = useState(false);
  const [representativeApplied, setRepresentativeApplied] = useState(false);
  const [descriptionJob, setDescriptionJob] = useState<Job | null>(null);
  const [confirmation, setConfirmation] = useState<
    | { kind: "remove"; job: Job }
    | { kind: "disconnect" }
    | null
  >(null);
  const [descriptionOptions, setDescriptionOptions] =
    useState<DescriptionApplicationOptions>({
      layout: "clean",
      placement: "bottom",
      captionTitle: "",
      captionBody: "",
    });
  const [galleryFilter, setGalleryFilter] = useState<
    "review" | "temporary" | "applied" | "expired"
  >("review");
  const [models, setModels] = useState<CatalogModel[]>(FALLBACK_MODELS);
  const [modelId, setModelId] = useState(FALLBACK_MODELS[0].id);
  const [modelGender, setModelGender] = useState<"all" | "female" | "male">(
    "all",
  );
  const [modelQuery, setModelQuery] = useState("");
  const [modelCasting, setModelCasting] = useState("all");
  const [presets, setPresets] = useState<CatalogPreset[]>(FALLBACK_PRESETS);
  const [presetCode, setPresetCode] = useState<string>(FALLBACK_PRESETS[0].code);
  const [presetFraming, setPresetFraming] = useState("all");
  const [presetAngle, setPresetAngle] = useState("all");
  const [presetPoseType, setPresetPoseType] = useState("all");
  const [presetPresentation, setPresetPresentation] = useState<
    "recommended" | "all" | "feminine" | "masculine" | "universal"
  >("recommended");
  const [presetOcclusionSafe, setPresetOcclusionSafe] = useState(true);
  const [backgroundTone, setBackgroundTone] = useState<
    "bright" | "balanced" | "dark"
  >("bright");
  const [busy, setBusy] = useState(false);
  const [restoringJobId, setRestoringJobId] = useState<string | null>(null);
  const [applyingJobId, setApplyingJobId] = useState<string | null>(null);
  const [applicationFeedback, setApplicationFeedback] =
    useState<ApplicationFeedback | null>(null);
  const [generationFeedback, setGenerationFeedback] =
    useState<GenerationRequestFeedback | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [appliedJobIds, setAppliedJobIds] = useState<string[]>([]);
  const [removedJobIds, setRemovedJobIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const pendingJobRequest = useRef<{ fingerprint: string; key: string } | null>(
    null,
  );
  const generationActionLock = useRef(false);
  const applicationActionLock = useRef(false);
  const representativeActionLock = useRef(false);
  const jobStatusMemory = useRef<Map<string, string>>(new Map());
  const stalledJobNotifications = useRef<Set<string>>(new Set());
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const inspectorRef = useRef<HTMLElement | null>(null);

  function resetStudioScroll() {
    if (canvasRef.current) canvasRef.current.scrollTop = 0;
    if (!inspectorRef.current) return;
    inspectorRef.current.scrollTop = 0;
    const activeSection = inspectorRef.current.querySelector<HTMLElement>(
      ".inspector-section:not(.step-hidden)",
    );
    if (activeSection) activeSection.scrollTop = 0;
  }

  function upsertToast(toast: ToastItem) {
    setToasts((current) => [
      ...current.filter((item) => item.id !== toast.id),
      toast,
    ].slice(-4));
  }

  function productPageUrl(productNo: number) {
    const product = products.find((item) => item.productNo === productNo);
    return commerceProductUrl(
      session?.mallId ?? "",
      productNo,
      product?.productUrl,
    );
  }

  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = Date.now();
      setToasts((current) =>
        current.filter((toast) => !toast.expiresAt || toast.expiresAt > now),
      );
    }, 500);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (
      commercePlatform !== "cafe24" ||
      !billingPolicy?.purchaseEnabled
    ) {
      setCreditPackages([]);
      return;
    }
    let cancelled = false;
    api.cafe24CreditPackages()
      .then(({ packages }) => {
        if (!cancelled) setCreditPackages(packages);
      })
      .catch((reason) => {
        if (!cancelled)
          setError(reason instanceof Error ? reason.message : "CAFE24_BILLING_LOAD_FAILED");
      });
    return () => {
      cancelled = true;
    };
  }, [billingPolicy?.purchaseEnabled]);

  useEffect(() => {
    if (!session || commercePlatform !== "cafe24") return;
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const orderId = params.get("order");
    if (!payment || !orderId) return;
    params.delete("payment");
    params.delete("order");
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${params.size ? `?${params}` : ""}${window.location.hash}`,
    );
    let cancelled = false;
    let attempts = 0;
    const refresh = async () => {
      attempts += 1;
      try {
        const result = await api.reconcileCafe24CreditOrder(orderId);
        if (cancelled) return;
        if (result.payment.status === "payment_pending" && attempts < 10) {
          window.setTimeout(() => void refresh(), 2_000);
          return;
        }
        const refreshed = await api.credits();
        if (cancelled) return;
        setCredits(refreshed.credits);
        setBillingPolicy(refreshed.billing);
        upsertToast({
          id: `payment-${orderId}`,
          tone: result.payment.status === "paid" ? "success" : "error",
          title:
            result.payment.status === "paid"
              ? "크레딧 충전이 완료되었습니다"
              : result.payment.status === "refunded"
                ? "환불이 반영되었습니다"
                : "결제 확인이 지연되고 있습니다",
          message:
            result.payment.status === "paid"
              ? "구매한 크레딧을 바로 사용할 수 있어요."
              : "결제 내역은 Cafe24 결제 정보와 자동으로 다시 확인합니다.",
          expiresAt: Date.now() + 10_000,
        });
      } catch (reason) {
        if (!cancelled)
          setError(reason instanceof Error ? reason.message : "CAFE24_PAYMENT_VERIFY_FAILED");
      }
    };
    void refresh();
    return () => {
      cancelled = true;
    };
  }, [session]);

  useEffect(() => {
    if (!error) return;
    upsertToast({
      id: "global-error",
      tone: "error",
      title: "요청을 완료하지 못했습니다",
      message: friendlyError(error),
      expiresAt: Date.now() + 8_000,
    });
  }, [error]);

  useEffect(() => {
    if (!generationFeedback) return;
    const accepted = generationFeedback.phase === "accepted";
    const failed = generationFeedback.phase === "error";
    upsertToast({
      id: "generation-request",
      tone: failed ? "error" : accepted ? "success" : "working",
      title: failed
        ? "생성 요청을 확인해 주세요"
        : accepted
          ? "착용샷 제작을 시작했습니다"
          : "생성 요청을 등록하고 있습니다",
      message: generationFeedback.message,
      expiresAt: generationFeedback.phase === "submitting" ? null : Date.now() + 7_000,
    });
  }, [generationFeedback]);

  useEffect(() => {
    if (!applicationFeedback) return;
    const isSuccess = applicationFeedback.phase === "success";
    upsertToast({
      id: "product-application",
      tone:
        applicationFeedback.phase === "working"
          ? "working"
          : isSuccess
            ? "success"
            : "error",
      title: applicationFeedback.message,
      message:
        applicationFeedback.phase === "working"
          ? "완료될 때까지 안전하게 처리합니다. 다른 화면으로 이동해도 작업은 계속됩니다."
          : `상품 번호 ${applicationFeedback.productNo}`,
      expiresAt: applicationFeedback.phase === "working" ? null : Date.now() + 8_000,
      ...(isSuccess && applicationFeedback.action !== "remove"
        ? {
            actionLabel: "상품 페이지 확인",
            actionHref: productPageUrl(applicationFeedback.productNo),
          }
        : {}),
    });
  }, [applicationFeedback, products, session?.mallId]);

  useEffect(() => {
    const memory = jobStatusMemory.current;
    if (memory.size === 0) {
      jobs.forEach((job) => memory.set(job.id, job.status));
      return;
    }

    jobs.forEach((job) => {
      const previous = memory.get(job.id);
      memory.set(job.id, job.status);

      if (
        previous &&
        ["queued", "dispatching", "generating"].includes(previous) &&
        job.status === "generated"
      ) {
        upsertToast({
          id: `job-complete-${job.id}`,
          tone: "success",
          title: "착용샷이 완성되었습니다",
          message: "결과 갤러리에서 이미지를 확인하고 상품에 적용할 수 있어요.",
          expiresAt: Date.now() + 10_000,
          actionLabel: "결과 보기",
          actionView: "gallery",
        });
        stalledJobNotifications.current.delete(job.id);
      }

      if (
        previous &&
        ["queued", "dispatching", "generating"].includes(previous) &&
        job.status === "failed"
      ) {
        upsertToast({
          id: `job-failed-${job.id}`,
          tone: "error",
          title: "이번 이미지는 완성하지 못했습니다",
          message: "크레딧은 차감하지 않았습니다. 같은 설정으로 다시 시도할 수 있어요.",
          expiresAt: Date.now() + 10_000,
        });
        stalledJobNotifications.current.delete(job.id);
      }

      const updatedAt = job.updatedAt ? new Date(job.updatedAt).getTime() : 0;
      const isStalled =
        ["queued", "dispatching", "generating"].includes(job.status) &&
        updatedAt > 0 &&
        Date.now() - updatedAt >= 90_000;
      if (isStalled && !stalledJobNotifications.current.has(job.id)) {
        stalledJobNotifications.current.add(job.id);
        upsertToast({
          id: `job-stalled-${job.id}`,
          tone: "working",
          title: "예상보다 시간이 더 걸리고 있습니다",
          message: "작업은 중단되지 않았어요. 상태를 계속 확인하고 완료 즉시 알려드릴게요.",
          expiresAt: Date.now() + 10_000,
        });
      }
    });
  }, [jobs]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const naverTestEntryCode = params.get("naver_test_code");
    if (commercePlatform === "naver" && naverTestEntryCode) {
      void fetch(
        `/api/naver/test-entry?code=${encodeURIComponent(naverTestEntryCode)}`,
        { credentials: "same-origin" },
      )
        .then((entryResponse) => {
          if (!entryResponse.ok) throw new Error("NAVER_TEST_ENTRY_FAILED");
          window.location.replace("/?connected=1");
        })
        .catch(() => setError("NAVER_TEST_ENTRY_FAILED"));
      return;
    }
    const naverEntryToken = params.get("token");
    if (commercePlatform === "naver" && naverEntryToken) {
      window.location.replace(
        `/api/naver/entry?token=${encodeURIComponent(naverEntryToken)}`,
      );
      return;
    }
    const oauthError = params.get("oauth_error");
    if (oauthError) {
      setError(`OAUTH_${oauthError.toUpperCase()}`);
      params.delete("oauth_error");
      const query = params.toString();
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
      );
    }
    document.title = `Snapcast ${platformUi.edition}`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        `${platformUi.name} 상품에 스냅캐스트 AI 착용 이미지를 더하세요.`,
      );
    api
      .config()
      .then((config) => {
        if (config.platform !== commercePlatform)
          throw new Error("PLATFORM_CONFIG_MISMATCH");
        return api.session();
      })
      .then(async (auth) => {
        setCsrfToken(auth.csrfToken);
        setSession(auth.session);
        setFlags(auth.flags);
        const [categoryResult, jobResult, catalogResult, creditResult] = await Promise.allSettled([
          api.categories(),
          api.jobs(),
          api.catalog(),
          api.credits(),
        ]);
        if (categoryResult.status === "fulfilled") {
          setProductCategories(categoryResult.value.items);
          setProductCategoriesUnavailable(false);
        } else setProductCategoriesUnavailable(true);
        setProductCategoriesLoading(false);
        if (jobResult.status === "fulfilled") setJobs(jobResult.value.items);
        else
          setError(
            (current) =>
              current ??
              (jobResult.reason instanceof Error
                ? jobResult.reason.message
                : "JOBS_LOAD_FAILED"),
          );
        if (creditResult.status === "fulfilled") {
          setCredits(creditResult.value.credits);
          setBillingPolicy(creditResult.value.billing);
        }
        else
          setError((current) => current ?? "CREDITS_LOAD_FAILED");
        if (
          catalogResult.status === "fulfilled" &&
          catalogResult.value.presets.length
        ) {
          if (catalogResult.value.models.length) {
            setModels(catalogResult.value.models);
            setModelId(catalogResult.value.models[0].id);
          }
          setPresets(catalogResult.value.presets);
          setPresetCode(catalogResult.value.presets[0].code);
        } else if (catalogResult.status === "rejected") {
          setError(
            (current) => current ?? "촬영 프리셋을 불러오지 못했습니다.",
          );
        }
      })
      .catch((reason) => {
        if (
          reason instanceof Error &&
          !["AUTH_REQUIRED", "SESSION_EXPIRED"].includes(reason.message)
        )
          setError(reason.message);
      });
  }, []);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoadingProducts(true);
      setProductLoadError(null);
      try {
        const data = await api.products({
          query: productQuery,
          categoryNo: productCategoryNo,
          status: productStatus,
        });
        if (cancelled) return;
        setProducts(data.items);
        setNextProductOffset(data.nextOffset);
        setError((current) =>
          current === "NAVER_COMMERCE_API_FAILED" ||
          current === "PRODUCTS_LOAD_FAILED"
            ? null
            : current,
        );
      } catch (reason) {
        if (cancelled) return;
        const code =
          reason instanceof Error ? reason.message : "PRODUCTS_LOAD_FAILED";
        setProductLoadError(code);
        setError(code);
      } finally {
        if (!cancelled) setLoadingProducts(false);
      }
    }, 280);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [session, productQuery, productCategoryNo, productStatus, productReloadKey]);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    api
      .productSummary()
      .then((summary) => {
        if (!cancelled) setProductSummary(summary);
      })
      .catch(() => {
        if (!cancelled) setProductSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [session]);

  const activeJobKey = useMemo(
    () =>
      jobs
        .filter((job) =>
          ["queued", "dispatching", "generating"].includes(job.status),
        )
        .map((job) => job.id)
        .sort()
        .join(","),
    [jobs],
  );

  useEffect(() => {
    const activeIds = activeJobKey ? activeJobKey.split(",") : [];
    if (activeIds.length === 0) return;
    let refreshing = false;
    const refresh = async () => {
      if (document.visibilityState !== "visible" || refreshing) return;
      refreshing = true;
      try {
        const [updates, creditResult] = await Promise.all([
          Promise.all(activeIds.map((id) => api.getJob(id))),
          api.credits(),
        ]);
        setJobs((current) =>
          current.map(
            (job) => updates.find((item) => item.job.id === job.id)?.job ?? job,
          ),
        );
        setCredits(creditResult.credits);
        setBillingPolicy(creditResult.billing);
      } catch (reason) {
        if (
          reason instanceof Error &&
          reason.message !== "CAFE24_TOKEN_REFRESH_BUSY"
        )
          setError(
            reason instanceof Error
              ? reason.message
              : "작업 상태를 확인하지 못했습니다.",
          );
      } finally {
        refreshing = false;
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 2_000);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [activeJobKey]);

  useEffect(() => {
    if (!viewerJob) return;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setViewerJob(null);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [viewerJob]);

  useEffect(() => {
    if (!representativeJob) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !representativeBusy) {
        setRepresentativeJob(null);
        setRepresentativePreview(null);
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [representativeBusy, representativeJob]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [view]);

  const selectedModel = useMemo(
    () => models.find((model) => model.id === modelId) ?? models[0],
    [modelId, models],
  );
  const selectedPreset = useMemo(
    () => presets.find((preset) => preset.code === presetCode) ?? presets[0],
    [presetCode, presets],
  );
  const evidenceCompatiblePresets = useMemo(
    () =>
      presets.filter((preset) =>
        garmentView === "both"
          ? true
          : garmentView === "back"
            ? preset.viewAngle === "back"
            : preset.viewAngle !== "back",
      ),
    [garmentView, presets],
  );
  const visiblePresets = useMemo(() => {
    const recommendedPresentation =
      selectedModel && modelGenderCode(selectedModel) === "male"
        ? "masculine"
        : "feminine";
    const filtered = evidenceCompatiblePresets.filter((preset) => {
      if (preset.poseType === "walking") return false;
      if (presetFraming !== "all" && preset.framing !== presetFraming)
        return false;
      if (presetAngle !== "all" && preset.viewAngle !== presetAngle)
        return false;
      if (presetPoseType !== "all" && preset.poseType !== presetPoseType)
        return false;
      if (
        presetPresentation === "recommended" &&
        ![recommendedPresentation, "universal"].includes(
          preset.posePresentation,
        )
      )
        return false;
      if (
        !["recommended", "all"].includes(presetPresentation) &&
        preset.posePresentation !== presetPresentation
      )
        return false;
      if (
        presetOcclusionSafe &&
        preset.occlusion.length > 0 &&
        !preset.occlusion.includes("none")
      )
        return false;
      return true;
    });
    return sortPresetsForStudio(filtered);
  }, [
    presetAngle,
    presetFraming,
    presetOcclusionSafe,
    presetPoseType,
    presetPresentation,
    evidenceCompatiblePresets,
    selectedModel,
  ]);
  const visiblePresetGroups = useMemo(
    () => groupPresetsByFraming(visiblePresets),
    [visiblePresets],
  );
  const presetFramingOptions = useMemo(() => {
    const available = new Set(
      evidenceCompatiblePresets.map((preset) => preset.framing),
    );
    return [
      ["all", "전체"],
      ...STUDIO_FRAMING_ORDER
        .filter((value) => available.has(value))
        .map((value) => [value, STUDIO_FRAMING_LABELS[value]]),
    ];
  }, [evidenceCompatiblePresets]);
  const presetAngleOptions = useMemo(() => {
    const labels: Record<string, string> = {
      front: "정면",
      "three-quarter": "3/4",
      "three-quarter-left": "좌 3/4",
      "three-quarter-right": "우 3/4",
      side: "측면",
      "side-left": "좌측면",
      "side-right": "우측면",
      back: "후면",
    };
    return [
      ["all", "전체 시점"],
      ...Object.keys(labels)
        .filter((value) =>
          evidenceCompatiblePresets.some(
            (preset) => preset.viewAngle === value,
          ),
        )
        .map((value) => [value, labels[value]]),
    ];
  }, [evidenceCompatiblePresets]);
  const presetPoseOptions = useMemo(() => {
    const labels: Record<string, string> = {
      standing: "서기",
      walking: "워킹",
      sitting: "앉기",
    };
    return [
      ["all", "전체 동작"],
      ...Object.keys(labels)
        .filter((value) => presets.some((preset) => preset.poseType === value))
        .map((value) => [value, labels[value]]),
    ];
  }, [presets]);

  useEffect(() => {
    if (
      visiblePresets.length > 0 &&
      !visiblePresets.some((preset) => preset.code === presetCode)
    )
      setPresetCode(visiblePresets[0].code);
  }, [presetCode, visiblePresets]);
  const visibleModels = useMemo(
    () =>
      models.filter(
        (model) => {
          if (modelGender !== "all" && modelGenderCode(model) !== modelGender)
            return false;
          if (
            modelCasting !== "all" &&
            (model.castingMoodKey ?? "natural_editorial") !== modelCasting
          )
            return false;
          const query = modelQuery.trim().toLocaleLowerCase("ko");
          if (!query) return true;
          return [
            model.name,
            model.detail,
            model.region,
            model.castingMoodLabel,
            ...(model.styleMood ?? []),
          ]
            .filter(Boolean)
            .join(" ")
            .toLocaleLowerCase("ko")
            .includes(query);
        },
      ),
    [modelCasting, modelGender, modelQuery, models],
  );
  const modelCastingOptions = useMemo(() => {
    const labels: Record<string, string> = {
      clean_minimal: "클린 미니멀",
      modern_edge: "모던 에지",
      soft_luxury: "소프트 럭셔리",
      classic_tailoring: "클래식 테일러링",
      sculptural_runway: "스컬프처럴 런웨이",
      natural_editorial: "내추럴 에디토리얼",
    };
    return [
      ["all", "전체 무드"],
      ...Array.from(new Set(models.map((model) => model.castingMoodKey ?? "natural_editorial")))
        .sort()
        .map((value) => [value, labels[value] ?? value]),
    ];
  }, [models]);

  const completedJobs = useMemo(
    () => jobs.filter((job) => job.status === "generated"),
    [jobs],
  );
  const photographedProductCount = useMemo(
    () => new Set(completedJobs.map((job) => job.productNo)).size,
    [completedJobs],
  );
  const modelUsage = useMemo(() => {
    const counts = new Map<string, { count: number; lastIndex: number }>();
    completedJobs.forEach((job, index) => {
      if (!job.modelKey) return;
      const current = counts.get(job.modelKey);
      counts.set(job.modelKey, {
        count: (current?.count ?? 0) + 1,
        lastIndex: current?.lastIndex ?? index,
      });
    });
    return [...counts.entries()]
      .map(([id, usage]) => ({
        model: models.find((model) => model.id === id),
        ...usage,
      }))
      .filter((item): item is typeof item & { model: CatalogModel } => Boolean(item.model))
      .sort((left, right) => right.count - left.count || left.lastIndex - right.lastIndex);
  }, [completedJobs, models]);
  const recentModels = useMemo(() => {
    const seen = new Set<string>();
    return completedJobs
      .map((job) => job.modelKey)
      .filter((id): id is string => Boolean(id))
      .filter((id) => (seen.has(id) ? false : (seen.add(id), true)))
      .map((id) => models.find((model) => model.id === id))
      .filter((model): model is CatalogModel => Boolean(model))
      .slice(0, 4);
  }, [completedJobs, models]);
  const quickModels = useMemo(() => {
    const seen = new Set<string>();
    return [
      ...recentModels.map((model) => ({ model, reason: "최근 사용" })),
      ...modelUsage.map(({ model, count }) => ({ model, reason: `${count}회 사용` })),
    ]
      .filter(({ model }) => (seen.has(model.id) ? false : (seen.add(model.id), true)))
      .slice(0, 5);
  }, [modelUsage, recentModels]);
  const modelQuickChoices = useMemo(
    () => {
      const seen = new Set<string>();
      return [
        ...quickModels,
        ...models.map((model) => ({ model, reason: "추천 모델" })),
      ]
        .filter(({ model }) =>
          seen.has(model.id) ? false : (seen.add(model.id), true),
        )
        .slice(0, 5);
    },
    [models, quickModels],
  );
  const presetUsage = useMemo(() => {
    const counts = new Map<string, { count: number; lastIndex: number }>();
    completedJobs.forEach((job, index) => {
      if (!job.presetCode) return;
      const current = counts.get(job.presetCode);
      counts.set(job.presetCode, {
        count: (current?.count ?? 0) + 1,
        lastIndex: current?.lastIndex ?? index,
      });
    });
    return [...counts.entries()]
      .map(([code, usage]) => ({
        preset: presets.find((preset) => preset.code === code),
        ...usage,
      }))
      .filter((item): item is typeof item & { preset: CatalogPreset } => Boolean(item.preset))
      .sort((left, right) => right.count - left.count || left.lastIndex - right.lastIndex);
  }, [completedJobs, presets]);
  const recentPresets = useMemo(() => {
    const seen = new Set<string>();
    return completedJobs
      .map((job) => job.presetCode)
      .filter((code): code is string => Boolean(code))
      .filter((code) => (seen.has(code) ? false : (seen.add(code), true)))
      .map((code) => presets.find((preset) => preset.code === code))
      .filter((preset): preset is CatalogPreset => Boolean(preset))
      .slice(0, 4);
  }, [completedJobs, presets]);
  const quickPresets = useMemo(() => {
    const seen = new Set<string>();
    return [
      ...recentPresets.map((preset) => ({ preset, reason: "최근 사용" })),
      ...presetUsage.map(({ preset, count }) => ({ preset, reason: `${count}회 사용` })),
    ]
      .filter(({ preset }) =>
        seen.has(preset.code) ? false : (seen.add(preset.code), true),
      )
      .slice(0, 5);
  }, [presetUsage, recentPresets]);
  const presetQuickChoices = useMemo(
    () => {
      const seen = new Set<string>();
      return [
        ...quickPresets,
        ...presets.map((preset) => ({ preset, reason: "추천 포즈" })),
      ]
        .filter(({ preset }) =>
          seen.has(preset.code) ? false : (seen.add(preset.code), true),
        )
        .slice(0, 5);
    },
    [presets, quickPresets],
  );
  const recentProducts = useMemo(() => {
    const seen = new Set<number>();
    const fromJobs = jobs
      .map((job) => job.productNo)
      .filter((productNo) =>
        seen.has(productNo) ? false : (seen.add(productNo), true),
      )
      .map((productNo) => products.find((product) => product.productNo === productNo))
      .filter((product): product is Product => Boolean(product));
    return (fromJobs.length ? fromJobs : products).slice(0, 6);
  }, [jobs, products]);

  function chooseQuickModel(model: CatalogModel, fromDashboard = false) {
    setPersonMode("provided_model");
    setModelId(model.id);
    setModelGender("all");
    setModelQuery("");
    setModelCasting("all");
    if (!fromDashboard) return;
    setView("studio");
    setStudioStep(selected && selectedOutfit && outfitConfirmed ? "model" : "product");
    upsertToast({
      id: `quick-model-${model.id}`,
      tone: "success",
      title: `${model.name} 모델을 선택했어요`,
      message: "상품과 의상을 고르면 이 모델로 바로 촬영을 이어갈 수 있어요.",
      expiresAt: Date.now() + 6_000,
    });
  }

  function chooseQuickPreset(preset: CatalogPreset, fromDashboard = false) {
    setPresetCode(preset.code);
    setPresetFraming("all");
    setPresetAngle("all");
    setPresetPoseType("all");
    setPresetPresentation("all");
    setPresetOcclusionSafe(false);
    if (!fromDashboard) return;
    setView("studio");
    setStudioStep(selected && selectedOutfit && outfitConfirmed ? "style" : "product");
    upsertToast({
      id: `quick-preset-${preset.code}`,
      tone: "success",
      title: `${preset.title} 포즈를 선택했어요`,
      message: "상품과 의상을 고르면 선택한 포즈를 유지한 채 촬영을 이어갑니다.",
      expiresAt: Date.now() + 6_000,
    });
  }

  function filterModels(gender: "all" | "female" | "male") {
    setModelGender(gender);
    const first = models.find(
      (model) => gender === "all" || modelGenderCode(model) === gender,
    );
    if (
      first &&
      !models.find(
        (model) =>
          model.id === modelId &&
          (gender === "all" || modelGenderCode(model) === gender),
      )
    )
      setModelId(first.id);
  }

  function chooseOutfit(candidate: OutfitCandidate) {
    setReshootSource(null);
    setSelectedOutfit(candidate);
    setGarmentView("front");
    setOutfitConfirmed(candidate.confidence === "high");
  }

  async function chooseProduct(product: Product) {
    if (!product.imageUrl) return;
    setReshootSource(null);
    setSelected(product);
    setSelectedOutfit(null);
    setGarmentView("front");
    setOutfitConfirmed(false);
    setOutfitCandidates([]);
    setLoadingOutfits(true);
    setError(null);
    try {
      const data = await api.outfitCandidates(product.productNo);
      setOutfitCandidates(data.items);
      const recommended =
        data.items.find((item) => item.confidence === "high") ??
        data.items[0] ??
        null;
      setSelectedOutfit(recommended);
      setOutfitConfirmed(recommended?.confidence === "high");
      setStudioStep("outfit");
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "OUTFIT_CANDIDATES_LOAD_FAILED",
      );
    } finally {
      setLoadingOutfits(false);
      window.requestAnimationFrame(() => {
        resetStudioScroll();
      });
    }
  }

  async function uploadOutfit(file: File | undefined) {
    if (!file) return;
    setUploadingOutfit(true);
    setError(null);
    try {
      const { upload } = await api.upload("outfit", file);
      const uploadedOutfit: OutfitSelection = {
        id: upload.uploadId,
        source: "upload",
        url: upload.previewUrl,
        label: "직접 업로드",
        confidence: "high",
        warnings: [],
        uploadId: upload.uploadId,
      };
      setReshootSource(null);
      setSelectedOutfit(uploadedOutfit);
      setGarmentView("front");
      setOutfitConfirmed(true);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "OUTFIT_UPLOAD_FAILED",
      );
    } finally {
      setUploadingOutfit(false);
    }
  }

  async function uploadPerson(file: File | undefined) {
    if (!file) return;
    setUploadingPerson(true);
    setError(null);
    try {
      const { upload } = await api.upload("person", file);
      setPersonUpload(upload);
      setPersonalConsent(false);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "PERSON_UPLOAD_FAILED",
      );
    } finally {
      setUploadingPerson(false);
    }
  }

  function choosePersonMode(mode: PersonMode) {
    setPersonMode(mode);
  }

  async function generate() {
    if (generationActionLock.current) return;
    if (!selected?.imageUrl || !selectedOutfit) {
      setGenerationFeedback({
        phase: "error",
        message: "상품과 의상 원본을 확인한 뒤 다시 시도해 주세요.",
      });
      return;
    }
    generationActionLock.current = true;
    setBusy(true);
    setError(null);
    setGenerationFeedback({
      phase: "submitting",
      message: "선택한 의상·모델·촬영 설정을 확인하고 있어요.",
    });
    try {
      if (
        personMode !== "provided_model" &&
        (!personUpload || !personalConsent)
      )
        throw new Error("PERSONAL_CONSENT_REQUIRED");
      const reusableSource = reusableReshootSource(
        reshootSource,
        selected.productNo,
      );
      const fingerprint = `${selected.productNo}:${selectedOutfit.id}:${reusableSource?.jobId ?? "new"}:${personMode}:${personUpload?.uploadId ?? modelId}:${presetCode}:${backgroundTone}`;
      if (pendingJobRequest.current?.fingerprint !== fingerprint)
        pendingJobRequest.current = { fingerprint, key: crypto.randomUUID() };
      const outfit =
        selectedOutfit.source === "upload"
          ? {
              source: "upload",
              uploadId: selectedOutfit.uploadId,
              view: garmentView,
            }
          : {
              source: selectedOutfit.source,
              candidateId: selectedOutfit.id,
              imageUrl: selectedOutfit.url,
              view: garmentView,
            };
      const { job } = await api.createJob({
        productNo: selected.productNo,
        outfit,
        ...(reusableSource
          ? { reshootSourceJobId: reusableSource.jobId }
          : {}),
        mode: personMode,
        ...(personMode === "provided_model"
          ? { modelKey: modelId }
          : {
              personUploadId: personUpload!.uploadId,
              consentVersion: "personal-image-v1",
            }),
        presetCode,
        backgroundTone,
        idempotencyKey: pendingJobRequest.current.key,
      });
      pendingJobRequest.current = null;
      setReshootSource(null);
      setJobs((current) => [
        job,
        ...current.filter((item) => item.id !== job.id),
      ]);
      const refreshedCredits = await api.credits();
      setCredits(refreshedCredits.credits);
      setGenerationFeedback({
        phase: "accepted",
        message: "생성 요청이 접수됐어요. 진행 상황을 갤러리에서 계속 보여드릴게요.",
      });
      setGalleryFilter("review");
      setView("gallery");
      window.setTimeout(
        () =>
          setGenerationFeedback((current) =>
            current?.phase === "accepted" ? null : current,
          ),
        6_000,
      );
    } catch (reason) {
      const code =
        reason instanceof Error ? reason.message : "GENERATION_REQUEST_FAILED";
      setGenerationFeedback({
        phase: "error",
        message:
          code === "INTERNAL_ERROR"
            ? "생성 요청을 등록하지 못했어요. 선택한 설정은 그대로 유지됐습니다. 잠시 후 다시 시도해 주세요."
            : friendlyError(code),
      });
    } finally {
      generationActionLock.current = false;
      setBusy(false);
    }
  }

  async function loadMoreProducts() {
    if (nextProductOffset === null) return;
    setLoadingProducts(true);
    setError(null);
    try {
      const data = await api.products({
        offset: nextProductOffset,
        query: productQuery,
        categoryNo: productCategoryNo,
        status: productStatus,
      });
      setProducts((current) => [
        ...current,
        ...data.items.filter(
          (item) =>
            !current.some((existing) => existing.productNo === item.productNo),
        ),
      ]);
      setNextProductOffset(data.nextOffset);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "상품을 더 불러오지 못했습니다.",
      );
    } finally {
      setLoadingProducts(false);
    }
  }

  function openDescriptionApplication(job: Job) {
    setDescriptionJob(job);
    setDescriptionOptions({
      layout: "clean",
      placement: "bottom",
      captionTitle: "",
      captionBody: "",
    });
  }

  async function apply(
    job: Job,
    options: DescriptionApplicationOptions,
  ) {
    if (applicationActionLock.current) return;
    applicationActionLock.current = true;
    setApplyingJobId(job.id);
    setError(null);
    setApplicationFeedback({
      action: "apply",
      phase: "working",
      productNo: job.productNo,
      message: `${platformUi.name} 이미지 공간에 업로드하고 있어요`,
    });
    const writingTimer = window.setTimeout(
      () =>
        setApplicationFeedback((current) =>
          current?.action === "apply" && current.phase === "working"
            ? { ...current, message: "상품 상세설명에 이미지를 추가하고 있어요" }
            : current,
        ),
      1_200,
    );
    const verifyingTimer = window.setTimeout(
      () =>
        setApplicationFeedback((current) =>
          current?.action === "apply" && current.phase === "working"
            ? { ...current, message: `${platformUi.name} 반영 결과를 확인하고 있어요` }
            : current,
        ),
      3_000,
    );
    try {
      await api.applyJob(job.id, options);
      setJobs((current) =>
        current.map((item) =>
          item.id === job.id
            ? {
                ...item,
                applicationStatus: "applied",
                commercePersistenceStatus: "stored",
                cafe24PersistenceStatus: "stored",
              }
            : item,
        ),
      );
      setViewerJob((current) =>
        current?.id === job.id
          ? {
              ...current,
              applicationStatus: "applied",
              commercePersistenceStatus: "stored",
              cafe24PersistenceStatus: "stored",
            }
          : current,
      );
      setAppliedJobIds((current) => [...new Set([...current, job.id])]);
      setRemovedJobIds((current) => current.filter((id) => id !== job.id));
      setApplicationFeedback({
        action: "apply",
        phase: "success",
        productNo: job.productNo,
        message: "상품 상세에 착용 이미지를 추가했어요",
      });
      setDescriptionJob(null);
      window.setTimeout(() => setApplicationFeedback(null), 5_000);
    } catch (reason) {
      setApplicationFeedback({
        action: "apply",
        phase: "error",
        productNo: job.productNo,
        message: "상품 상세에 추가하지 못했어요",
      });
      setError(
        reason instanceof Error
          ? reason.message
          : "상품 상세에 적용하지 못했습니다.",
      );
    } finally {
      window.clearTimeout(writingTimer);
      window.clearTimeout(verifyingTimer);
      applicationActionLock.current = false;
      setApplyingJobId(null);
    }
  }

  async function openRepresentative(job: Job) {
    if (representativeActionLock.current) return;
    representativeActionLock.current = true;
    setRepresentativeJob(job);
    setRepresentativePreview(null);
    setRepresentativeApplied(false);
    setRepresentativeBusy(true);
    setError(null);
    try {
      const result = await api.representativePreview(job.id);
      setRepresentativePreview(result.preview);
      setJobs((current) =>
        current.map((item) =>
          item.id === job.id
            ? {
                ...item,
                commercePersistenceStatus: "stored",
                cafe24PersistenceStatus: "stored",
              }
            : item,
        ),
      );
      setViewerJob((current) =>
        current?.id === job.id
          ? {
              ...current,
              commercePersistenceStatus: "stored",
              cafe24PersistenceStatus: "stored",
            }
          : current,
      );
    } catch (reason) {
      setRepresentativeJob(null);
      setError(
        friendlyError(
          reason instanceof Error ? reason.message : "THUMBNAIL_PREVIEW_FAILED",
        ),
      );
    } finally {
      representativeActionLock.current = false;
      setRepresentativeBusy(false);
    }
  }

  async function confirmRepresentative() {
    if (
      !representativeJob ||
      !representativePreview ||
      representativeActionLock.current
    ) return;
    representativeActionLock.current = true;
    setRepresentativeBusy(true);
    setApplicationFeedback({
      action: "thumbnail",
      phase: "working",
      productNo: representativeJob.productNo,
      message: "기존 대표이미지를 보관하고 새 이미지 반영을 확인하고 있어요.",
    });
    try {
      await api.applyRepresentativeImage(representativeJob.id, {
        mode: representativeMode,
        expectedDigest: representativePreview.currentDigest,
      });
      setRepresentativeApplied(true);
      setApplicationFeedback({
        action: "thumbnail",
        phase: "success",
        productNo: representativeJob.productNo,
        message: "대표이미지 교체와 실제 반영 확인을 마쳤어요.",
      });
    } catch (reason) {
      const message = friendlyError(
        reason instanceof Error ? reason.message : "THUMBNAIL_APPLY_FAILED",
      );
      setApplicationFeedback({
        action: "thumbnail",
        phase: "error",
        productNo: representativeJob.productNo,
        message,
      });
      setError(message);
    } finally {
      representativeActionLock.current = false;
      setRepresentativeBusy(false);
    }
  }

  async function rollbackRepresentative() {
    if (!representativeJob || representativeActionLock.current) return;
    representativeActionLock.current = true;
    setRepresentativeBusy(true);
    setApplicationFeedback({
      action: "thumbnail",
      phase: "working",
      productNo: representativeJob.productNo,
      message: "교체 전 대표이미지로 안전하게 되돌리고 있어요.",
    });
    try {
      await api.rollbackRepresentativeImage(representativeJob.id);
      setRepresentativeApplied(false);
      setApplicationFeedback({
        action: "thumbnail",
        phase: "success",
        productNo: representativeJob.productNo,
        message: "이전 대표이미지로 복원했어요.",
      });
      setRepresentativeJob(null);
      setRepresentativePreview(null);
    } catch (reason) {
      const message = friendlyError(
        reason instanceof Error ? reason.message : "THUMBNAIL_ROLLBACK_FAILED",
      );
      setApplicationFeedback({
        action: "thumbnail",
        phase: "error",
        productNo: representativeJob.productNo,
        message,
      });
      setError(message);
    } finally {
      representativeActionLock.current = false;
      setRepresentativeBusy(false);
    }
  }

  async function reformat(job: Job) {
    if (applicationActionLock.current) return;
    applicationActionLock.current = true;
    setApplyingJobId(job.id);
    setError(null);
    setApplicationFeedback({
      action: "layout",
      phase: "working",
      productNo: job.productNo,
      message: "상세페이지 본문 폭에 맞춰 다시 정렬하고 있어요",
    });
    try {
      await api.applyJob(job.id, {
        layout: "clean",
        placement: "bottom",
      });
      setJobs((current) =>
        current.map((item) =>
          item.id === job.id ? { ...item, applicationStatus: "applied" } : item,
        ),
      );
      setViewerJob((current) =>
        current?.id === job.id
          ? { ...current, applicationStatus: "applied" }
          : current,
      );
      setAppliedJobIds((current) => [...new Set([...current, job.id])]);
      setApplicationFeedback({
        action: "layout",
        phase: "success",
        productNo: job.productNo,
        message: "이미지를 가운데 정렬하고 본문 폭에 맞췄어요",
      });
      window.setTimeout(() => setApplicationFeedback(null), 5_000);
    } catch (reason) {
      setApplicationFeedback({
        action: "layout",
        phase: "error",
        productNo: job.productNo,
        message: "상세 이미지 정렬을 변경하지 못했어요",
      });
      setError(
        reason instanceof Error ? reason.message : "레이아웃을 변경하지 못했습니다.",
      );
    } finally {
      applicationActionLock.current = false;
      setApplyingJobId(null);
    }
  }

  async function remove(job: Job) {
    if (applicationActionLock.current) return;
    setConfirmation(null);
    applicationActionLock.current = true;
    setApplyingJobId(job.id);
    setError(null);
    setApplicationFeedback({
      action: "remove",
      phase: "working",
      productNo: job.productNo,
      message: "상품 상세에서 착용 이미지를 제거하고 있어요",
    });
    try {
      await api.removeJob(job.id);
      setJobs((current) =>
        current.map((item) =>
          item.id === job.id
            ? { ...item, applicationStatus: "removed" }
            : item,
        ),
      );
      setViewerJob((current) =>
        current?.id === job.id
          ? { ...current, applicationStatus: "removed" }
          : current,
      );
      setRemovedJobIds((current) => [...new Set([...current, job.id])]);
      setAppliedJobIds((current) => current.filter((id) => id !== job.id));
      setApplicationFeedback({
        action: "remove",
        phase: "success",
        productNo: job.productNo,
        message: "상품 상세에서 착용 이미지를 제거했어요",
      });
      window.setTimeout(() => setApplicationFeedback(null), 5_000);
    } catch (reason) {
      setApplicationFeedback({
        action: "remove",
        phase: "error",
        productNo: job.productNo,
        message: "상품 상세에서 제거하지 못했어요",
      });
      setError(
        reason instanceof Error
          ? reason.message
          : "상품 상세에서 제거하지 못했습니다.",
      );
    } finally {
      applicationActionLock.current = false;
      setApplyingJobId(null);
    }
  }

  async function disconnect() {
    setConfirmation(null);
    setBusy(true);
    setError(null);
    try {
      await api.disconnect();
      location.reload();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "연결을 해제하지 못했습니다.",
      );
      setBusy(false);
    }
  }

  async function restoreJobSetup(job: Job) {
    if (restoringJobId) return;
    setRestoringJobId(job.id);
    try {
      let product = products.find((item) => item.productNo === job.productNo);
      const outfit = job.outfit;
      if (!product) {
        product = {
          productNo: job.productNo,
          name: `이전 촬영 상품 NO. ${job.productNo}`,
          imageUrl: outfit?.imageUrl ?? job.resultUrl,
          productUrl: commerceProductUrl(
            session?.mallId ?? "",
            job.productNo,
          ),
          display: "F",
          selling: "F",
        };
      }
      setSelected(product);

      let restoredOutfitId: string;
      if (outfit?.source === "upload" && outfit.uploadId) {
        restoredOutfitId = outfit.uploadId;
        setSelectedOutfit({
          id: outfit.uploadId,
          source: "upload",
          uploadId: outfit.uploadId,
          url: `/api/uploads/${outfit.uploadId}/content`,
          label: "이전 촬영의 업로드 의상",
          confidence: "high",
          warnings: [],
        });
      } else if (
        outfit?.source &&
        outfit.source !== "upload" &&
        outfit.imageUrl
      ) {
        restoredOutfitId = outfit.candidateId ?? `reshoot-${job.id}`;
        setSelectedOutfit({
          id: restoredOutfitId,
          source: outfit.source,
          url: outfit.imageUrl,
          label: "이전 촬영의 의상",
          confidence: "high",
          warnings: [],
        });
      } else throw new Error("RESHOOT_OUTFIT_NOT_FOUND");
      setGarmentView(outfit?.view ?? "front");
      setReshootSource({
        jobId: job.id,
        productNo: job.productNo,
      });

      setModelGender("all");
      setModelQuery("");
      setModelCasting("all");
      setPresetFraming("all");
      setPresetAngle("all");
      setPresetPoseType("all");
      setPresetPresentation("all");
      setPresetOcclusionSafe(false);
      let resumeStep: StudioStep = "style";
      if (job.mode === "provided_model") {
        setPersonMode("provided_model");
        if (job.modelKey && models.some((model) => model.id === job.modelKey))
          setModelId(job.modelKey);
        else resumeStep = "model";
      } else {
        setPersonMode("personal_face");
        setPersonUpload(null);
        setPersonalConsent(false);
        resumeStep = "model";
      }
      if (job.presetCode && presets.some((preset) => preset.code === job.presetCode))
        setPresetCode(job.presetCode);
      else resumeStep = resumeStep === "model" ? "model" : "style";
      if (job.backgroundTone) setBackgroundTone(job.backgroundTone);
      setOutfitConfirmed(true);
      pendingJobRequest.current = null;
      setGenerationFeedback(null);
      setViewerJob(null);
      setView("studio");
      setStudioStep(resumeStep);
      upsertToast({
        id: `reshoot-${job.id}`,
        tone: "success",
        title: "이전 촬영 설정을 불러왔어요",
        message:
          resumeStep === "model"
              ? "상품과 의상을 복사했습니다. 모델을 선택한 뒤 포즈·배경을 자유롭게 바꿔 새로 촬영하세요."
              : "상품·의상·모델과 이전 포즈를 불러왔습니다. 포즈·프레이밍·배경을 확인하거나 바꾼 뒤 다음 단계로 이동하세요.",
        expiresAt: Date.now() + 8_000,
      });
      window.requestAnimationFrame(() => {
        resetStudioScroll();
      });
    } catch (reason) {
      upsertToast({
        id: `reshoot-error-${job.id}`,
        tone: "error",
        title: "이전 촬영 설정을 불러오지 못했어요",
        message: friendlyError(
          reason instanceof Error ? reason.message : "RESHOOT_RESTORE_FAILED",
        ),
        expiresAt: Date.now() + 8_000,
      });
    } finally {
      setRestoringJobId(null);
    }
  }

  async function purchaseCredits(packageId: string) {
    if (commercePlatform !== "cafe24" || purchasingPackageId) return;
    setPurchasingPackageId(packageId);
    setError(null);
    upsertToast({
      id: "credit-purchase",
      tone: "working",
      title: "Cafe24 결제창을 준비하고 있습니다",
      message: "주문이 만들어지면 Cafe24 공식 결제 화면으로 이동합니다.",
      expiresAt: null,
    });
    try {
      const order = await api.createCafe24CreditOrder(packageId);
      window.location.assign(order.confirmationUrl);
    } catch (reason) {
      setToasts((current) =>
        current.filter((toast) => toast.id !== "credit-purchase"),
      );
      setError(
        reason instanceof Error ? reason.message : "CAFE24_PAYMENT_CREATE_FAILED",
      );
      setPurchasingPackageId(null);
    }
  }

  if (!session) return <Login error={error} />;
  const activeJobs = jobs.filter((job) =>
    ["queued", "dispatching", "generating"].includes(job.status),
  );
  const generatedJobs = jobs.filter((job) => job.status === "generated");
  const galleryJobs = jobs.filter((job) =>
    galleryFilter === "expired"
      ? job.status === "expired"
      : galleryFilter === "applied"
        ? isAppliedStatus(job.applicationStatus)
        : galleryFilter === "temporary"
          ? job.status === "generated" && !isAppliedStatus(job.applicationStatus)
          : job.status !== "expired" && !isAppliedStatus(job.applicationStatus),
  );
  const viewerApplied = Boolean(
    viewerJob &&
      (isAppliedStatus(viewerJob.applicationStatus) ||
        appliedJobIds.includes(viewerJob.id)) &&
      !removedJobIds.includes(viewerJob.id),
  );
  const studioSteps: Array<{ key: StudioStep; number: string; label: string }> =
    [
      { key: "product", number: "01", label: "상품 선택" },
      { key: "outfit", number: "02", label: "의상 원본" },
      { key: "model", number: "03", label: "모델" },
      { key: "style", number: "04", label: "촬영 스타일" },
      { key: "engine", number: "05", label: "생성 확인" },
    ];
  const stepIndex = studioSteps.findIndex((step) => step.key === studioStep);
  const stepComplete = (step: StudioStep) =>
    step === "product"
      ? Boolean(selected)
      : step === "outfit"
        ? Boolean(selectedOutfit && outfitConfirmed)
        : step === "model"
          ? personMode === "provided_model" ||
            Boolean(personUpload && personalConsent)
          : step === "style"
            ? Boolean(presetCode)
            : true;
  const stepAvailable = (step: StudioStep) => {
    const target = studioSteps.findIndex((item) => item.key === step);
    return studioSteps.slice(0, target).every((item) => stepComplete(item.key));
  };
  const openStudioStep = (step: StudioStep) => {
    if (!stepAvailable(step)) return;
    setView("studio");
    setStudioStep(step);
    window.requestAnimationFrame(() => {
      resetStudioScroll();
    });
  };
  return (
    <div className="app-shell workspace-shell">
      <aside className="sidebar">
        <a className="brand sidebar-brand" href="/" aria-label={`Snapcast | ${platformUi.edition} 홈`}>
          <span className="sidebar-brand-logo" aria-hidden="true">
            <img src="/brand/snapcast-logo-white.png" alt="" />
          </span>
          <span className="brand-platform-divider" aria-hidden="true" />
          <span className="brand-platform-label">{platformUi.edition}</span>
        </a>
        <nav>
          <button
            className={view === "dashboard" ? "active" : ""}
            aria-current={view === "dashboard" ? "page" : undefined}
            onClick={() => setView("dashboard")}
          >
            <UiIcon name="home" />
            대시보드
          </button>
          <button
            className={view === "studio" ? "active" : ""}
            aria-current={view === "studio" ? "page" : undefined}
            onClick={() => openStudioStep(studioStep)}
          >
            <Icon name="spark" />
            스튜디오
          </button>
          <button
            className={view === "gallery" ? "active" : ""}
            aria-current={view === "gallery" ? "page" : undefined}
            onClick={() => setView("gallery")}
          >
            <Icon name="clock" />
            갤러리{activeJobs.length > 0 && <em>{activeJobs.length}</em>}
          </button>
          <button
            className={view === "tokens" ? "active" : ""}
            aria-current={view === "tokens" ? "page" : undefined}
            onClick={() => setView("tokens")}
          >
            <UiIcon name="credit" />
            토큰
            {credits && (
              <em className="credit-menu-balance">
                {credits.available.toLocaleString()}
              </em>
            )}
          </button>
          <button
            className={view === "settings" ? "active" : ""}
            aria-current={view === "settings" ? "page" : undefined}
            onClick={() => setView("settings")}
          >
            <Icon name="settings" />
            설정
          </button>
        </nav>
        <div className="side-foot">
          <span className="status-dot" />
          <strong>{session.displayName}</strong>
          <small>{platformUi.name} 연결됨</small>
        </div>
      </aside>
      <main className={`workspace-main view-${view}`}>
        <header className="workspace-header">
          <div>
            <span className="eyebrow">{platformUi.headerLabel}</span>
            <h1>
              {view === "dashboard"
                ? "스토어 대시보드"
                : view === "studio"
                ? (studioSteps[stepIndex]?.label ?? "AI 착용 스튜디오")
                : view === "gallery"
                  ? "결과 갤러리"
                  : view === "tokens"
                    ? "토큰과 사용 내역"
                    : "연결과 데이터"}
            </h1>
            <p className="workspace-description">
              {view === "dashboard"
                ? "최근 상품과 촬영 기록을 살펴보고 자주 쓰는 설정으로 빠르게 시작하세요."
                : view === "studio"
                ? "상품부터 모델·포즈·출시 적용까지 한 흐름으로 완성합니다."
                : view === "gallery"
                  ? "생성 결과의 진행 상태를 확인하고 상품에 바로 적용하세요."
                  : view === "tokens"
                    ? "생성 비용과 사용 내역을 투명하게 확인합니다."
                    : platformUi.settingsDescription}
            </p>
          </div>
          <div className="workspace-status">
            <span className="live-pill">{platformUi.connectedLabel}</span>
            <span className="header-credit-balance">
              <UiIcon name="credit" />
              {credits?.available.toLocaleString() ?? "—"} 크레딧
            </span>
            <b>{session.displayName}</b>
            <small>상품 연동 스튜디오</small>
          </div>
        </header>

        {view === "dashboard" && (
          <section className="dashboard-view">
            <div className="dashboard-hero">
              <div>
                <span className="dashboard-kicker">TODAY'S STUDIO</span>
                <h2>다음 상품 촬영을<br />가장 빠르게 시작하세요.</h2>
                <p>최근 선택과 누적 사용 기록을 바탕으로 익숙한 모델·포즈를 먼저 준비했습니다.</p>
              </div>
              <button
                className="dashboard-primary"
                onClick={() => {
                  setView("studio");
                  setStudioStep("product");
                }}
              >
                <UiIcon name="spark" /> 새 착용샷 만들기
                <UiIcon name="arrow-right" />
              </button>
            </div>

            <div className="dashboard-metrics" aria-label="최근 스튜디오 현황">
              <article>
                <span>AVAILABLE CREDIT</span>
                <strong>{credits?.available.toLocaleString() ?? "—"}</strong>
                <small>사용 가능한 크레딧</small>
              </article>
              <article>
                <span>COMPLETED</span>
                <strong>{completedJobs.length}</strong>
                <small>최근 완성 이미지</small>
              </article>
              <article>
                <span>IN PROGRESS</span>
                <strong>{activeJobs.length}</strong>
                <small>현재 진행 중인 촬영</small>
              </article>
              <article>
                <span>PUBLISHED</span>
                <strong>{jobs.filter((job) => isAppliedStatus(job.applicationStatus)).length}</strong>
                <small>상품 상세 적용 기록</small>
              </article>
            </div>

            <section className="dashboard-product-summary" aria-label={`${platformUi.name} 상품 요약`}>
              <header>
                <div>
                  <span>PRODUCT OVERVIEW</span>
                  <h3>상품 요약</h3>
                  <p>연결된 {platformUi.name} 상품의 운영 현황입니다.</p>
                </div>
                <button
                  onClick={() => {
                    setView("studio");
                    setStudioStep("product");
                  }}
                >
                  전체 상품 보기 <UiIcon name="arrow-right" />
                </button>
              </header>
              <div className="dashboard-product-summary-grid">
                <article>
                  <span>ALL PRODUCTS</span>
                  <strong>{productSummary?.total.toLocaleString() ?? "—"}</strong>
                  <small>전체 등록 상품</small>
                </article>
                <article>
                  <span>ON SALE</span>
                  <strong>{productSummary?.selling.toLocaleString() ?? "—"}</strong>
                  <small>진열·판매 중</small>
                </article>
                <article>
                  <span>HIDDEN</span>
                  <strong>{productSummary?.hidden.toLocaleString() ?? "—"}</strong>
                  <small>미진열 상품</small>
                </article>
                <article>
                  <span>PHOTOGRAPHED</span>
                  <strong>{photographedProductCount.toLocaleString()}</strong>
                  <small>착용샷 제작 상품</small>
                </article>
              </div>
            </section>

            <div className="dashboard-grid">
              <section className="dashboard-panel dashboard-results">
                <header>
                  <div><span>RECENT OUTPUT</span><h3>최근 생성물</h3></div>
                  <button onClick={() => setView("gallery")}>
                    전체 보기 <UiIcon name="arrow-right" />
                  </button>
                </header>
                {completedJobs.length ? (
                  <div className="dashboard-result-strip">
                    {completedJobs.slice(0, 5).map((job) => (
                      <button key={job.id} onClick={() => setViewerJob(job)}>
                        {job.resultUrl ? (
                          <img src={job.resultUrl} alt={`상품 ${job.productNo} 생성 결과`} />
                        ) : (
                          <span className="dashboard-image-empty"><UiIcon name="image" /></span>
                        )}
                        <span><b>NO. {job.productNo}</b><small>{job.createdAt ? new Date(job.createdAt).toLocaleDateString("ko-KR") : "최근 촬영"}</small></span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="dashboard-empty"><UiIcon name="gallery" /><span><b>아직 완성 이미지가 없습니다.</b><small>첫 착용샷을 만들면 여기에 바로 모아드려요.</small></span></div>
                )}
              </section>

              <section className="dashboard-panel dashboard-favorites">
                <header>
                  <div><span>FAVORITE CAST</span><h3>자주 쓰는 모델</h3></div>
                  <button onClick={() => { setView("studio"); setStudioStep(selected && selectedOutfit && outfitConfirmed ? "model" : "product"); }}>
                    모델 선택 <UiIcon name="arrow-right" />
                  </button>
                </header>
                <div className="dashboard-model-list">
                  {modelQuickChoices.slice(0, 4).map(({ model, reason }, index) => (
                    <button key={model.id} onClick={() => chooseQuickModel(model, true)}>
                      <em>{String(index + 1).padStart(2, "0")}</em>
                      <img
                        src={`/model-thumbnails/${model.id}.jpg`}
                        alt={model.name}
                        onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = model.image; }}
                      />
                      <span><b>{model.name}</b><small>{model.castingMoodLabel ?? model.detail}</small></span>
                      <strong>{reason}</strong>
                    </button>
                  ))}
                </div>
              </section>

              <section className="dashboard-panel dashboard-poses">
                <header>
                  <div><span>FAVORITE DIRECTION</span><h3>자주 쓰는 포즈</h3></div>
                  <button onClick={() => { setView("studio"); setStudioStep(selected && selectedOutfit && outfitConfirmed ? "style" : "product"); }}>
                    포즈 선택 <UiIcon name="arrow-right" />
                  </button>
                </header>
                <div className="dashboard-pose-list">
                  {presetQuickChoices.slice(0, 4).map(({ preset, reason }) => (
                    <button key={preset.code} onClick={() => chooseQuickPreset(preset, true)}>
                      <img src={preset.image} alt={preset.title} />
                      <span><b>{preset.title}</b><small>{preset.subtitle}</small></span>
                      <em>{reason}</em>
                    </button>
                  ))}
                </div>
              </section>

              <section className="dashboard-panel dashboard-products">
                <header>
                  <div><span>RECENT PRODUCTS</span><h3>최근 촬영 상품</h3></div>
                  <button onClick={() => { setView("studio"); setStudioStep("product"); }}>
                    전체 상품 <UiIcon name="arrow-right" />
                  </button>
                </header>
                <div className="dashboard-product-strip">
                  {recentProducts.map((product) => (
                    <button
                      key={product.productNo}
                      onClick={() => {
                        setView("studio");
                        void chooseProduct(product);
                      }}
                    >
                      {product.imageUrl && <img src={product.imageUrl} alt={product.name} />}
                      <span><small>NO. {product.productNo}</small><b>{product.name}</b></span>
                      <i><UiIcon name="arrow-right" /></i>
                    </button>
                  ))}
                </div>
              </section>
            </div>
          </section>
        )}

        {view === "studio" && (
          <nav className="studio-flow-stepper" aria-label="착용컷 생성 진행 단계">
            {studioSteps.map((step) => {
              const active = studioStep === step.key;
              const available = stepAvailable(step.key);
              const complete = available && stepComplete(step.key);
              return (
                <button
                  key={step.key}
                  className={[
                    active ? "active" : "",
                    complete ? "complete" : "",
                    available ? "available" : "upcoming",
                  ].join(" ")}
                  disabled={!available}
                  aria-current={active ? "step" : undefined}
                  onClick={() => openStudioStep(step.key)}
                >
                  <span className="studio-flow-marker">
                    {!active && complete ? "✓" : step.number}
                  </span>
                  <span className="studio-flow-copy">
                    <b>{step.label}</b>
                    <small>
                      {active ? "진행 중" : complete ? "완료" : "다음 단계"}
                    </small>
                  </span>
                </button>
              );
            })}
          </nav>
        )}

        {view === "studio" && (
          <section className="creation-workspace">
            <div className="creation-canvas" ref={canvasRef}>
              {studioStep === "product" || !selected ? (
                <div className="canvas-product-picker">
                  <div className="canvas-title">
                    <span>START WITH A PRODUCT</span>
                    <h2>착용컷을 만들 상품을 선택하세요.</h2>
                  </div>
                  <div className="product-discovery">
                    <label className="product-search">
                      <UiIcon name="search" />
                      <input
                        type="search"
                        value={productQuery}
                        onChange={(event) => setProductQuery(event.target.value)}
                        placeholder="상품명 또는 상품번호 검색"
                        aria-label="상품 검색"
                      />
                      {productQuery && (
                        <button
                          type="button"
                          aria-label="검색어 지우기"
                          onClick={() => setProductQuery("")}
                        >
                          <UiIcon name="close" />
                        </button>
                      )}
                    </label>
                    <label className="product-select">
                      <span><UiIcon name="filter" /> 카테고리</span>
                      <select
                        value={productCategoryNo ?? ""}
                        disabled={productCategoriesLoading || productCategoriesUnavailable}
                        onChange={(event) =>
                          setProductCategoryNo(
                            event.target.value ? Number(event.target.value) : null,
                          )
                        }
                        aria-label="상품 카테고리"
                      >
                        <option value="">
                          {productCategoriesLoading
                            ? "카테고리 불러오는 중"
                            : productCategoriesUnavailable
                              ? "카테고리 권한 재연결 필요"
                              : "전체 카테고리"}
                        </option>
                        {productCategories.map((category) => (
                          <option key={category.categoryNo} value={category.categoryNo}>
                            {`${"— ".repeat(Math.max(0, category.depth - 1))}${category.name}`}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="product-select">
                      <span><UiIcon name="filter" /> 상품 상태</span>
                      <select
                        value={productStatus}
                        onChange={(event) =>
                          setProductStatus(
                            event.target.value as "all" | "selling" | "hidden",
                          )
                        }
                        aria-label="상품 상태"
                      >
                        <option value="all">전체 상품</option>
                        <option value="selling">진열·판매 중</option>
                        <option value="hidden">진열 안 함</option>
                      </select>
                    </label>
                  </div>
                  <div className="product-result-meta">
                    <span>
                      {loadingProducts
                        ? "상품을 찾고 있습니다"
                        : productLoadError
                          ? "상품 목록을 불러오지 못했습니다"
                        : `${products.length}개 상품 표시`}
                    </span>
                    {(productQuery || productCategoryNo || productStatus !== "all") && (
                      <button
                        type="button"
                        onClick={() => {
                          setProductQuery("");
                          setProductCategoryNo(null);
                          setProductStatus("all");
                        }}
                      >
                        <UiIcon name="retry" /> 필터 초기화
                      </button>
                    )}
                  </div>
                  {loadingProducts && products.length === 0 ? (
                    <div className="product-loading-state">
                      <span className="loader" />
                      <b>상품 목록을 불러오고 있어요.</b>
                    </div>
                  ) : productLoadError && products.length === 0 ? (
                    <div className="product-empty-state product-error-state">
                      <UiIcon name="retry" />
                      <h3>상품 목록을 불러오지 못했습니다.</h3>
                      <p>{friendlyError(productLoadError)}</p>
                      <button
                        type="button"
                        onClick={() => setProductReloadKey((value) => value + 1)}
                      >
                        <UiIcon name="retry" /> 상품 다시 불러오기
                      </button>
                    </div>
                  ) : products.length === 0 ? (
                    <div className="product-empty-state">
                      <UiIcon name="search" />
                      <h3>조건에 맞는 상품이 없습니다.</h3>
                      <p>검색어나 카테고리, 상품 상태를 바꿔보세요.</p>
                      <button
                        type="button"
                        onClick={() => {
                          setProductQuery("");
                          setProductCategoryNo(null);
                          setProductStatus("all");
                        }}
                      >
                        <UiIcon name="retry" /> 전체 상품 다시 보기
                      </button>
                    </div>
                  ) : <div className="product-grid compact">
                    {products.map((product) => (
                      <button
                        className="product-card"
                        key={product.productNo}
                        onClick={() => void chooseProduct(product)}
                        disabled={!product.imageUrl}
                      >
                        <div className="product-image">
                          {product.imageUrl && (
                            <img src={product.imageUrl} alt="" />
                          )}
                          <i><UiIcon name="arrow-right" /></i>
                        </div>
                        <span className="product-no">
                          NO. {product.productNo}
                        </span>
                        <strong>{product.name}</strong>
                        <small className={product.display === "T" && product.selling === "T" ? "selling" : "hidden"}>
                          <i />
                          {product.display === "T" && product.selling === "T"
                            ? "진열·판매 중"
                            : "진열 안 함"}
                        </small>
                      </button>
                    ))}
                  </div>}
                  {productCategoriesUnavailable && (
                    <div className="category-permission-note">
                      <UiIcon name="filter" />
                      <span>
                        <b>카테고리 필터 사용을 위해 {platformUi.name} 재연결이 필요합니다.</b>
                        <small>상품 검색과 상태 필터는 지금도 사용할 수 있습니다.</small>
                      </span>
                      <a href={`/api/oauth/start?mall_id=${encodeURIComponent(session.mallId)}`}>
                        <UiIcon name="arrow-right" /> 권한 연결
                      </a>
                    </div>
                  )}
                  {nextProductOffset !== null && (
                    <button
                      className="load-more"
                      disabled={loadingProducts}
                      onClick={loadMoreProducts}
                    >
                      <UiIcon name="plus" />
                      {loadingProducts ? "불러오는 중…" : "상품 더 보기"}
                    </button>
                  )}
                </div>
              ) : (
                <div className="composition-stage">
                  <div className="stage-meta">
                    <span>선택 미리보기</span>
                    <small>선택을 바꾸면 이 구성이 함께 갱신됩니다.</small>
                  </div>
                  <div className="stage-visual editorial-board">
                    <figure
                      className="stage-card stage-outfit interactive"
                      role="button"
                      tabIndex={0}
                      aria-label="의상 원본 선택 단계로 이동"
                      onClick={() => openStudioStep("outfit")}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openStudioStep("outfit");
                        }
                      }}
                    >
                      <div className="stage-card-head">
                        <span>01</span>
                        <div><b>의상</b><small>착용할 원본</small></div>
                        <i><UiIcon name="arrow-right" /> 변경</i>
                      </div>
                      <div className="stage-card-media">
                        {selectedOutfit ? (
                          <img src={selectedOutfit.url} alt="선택 의상" />
                        ) : (
                          <div className="stage-placeholder">의상 이미지를 선택하세요</div>
                        )}
                      </div>
                      <figcaption>
                        <strong>{selectedOutfit?.label ?? "의상 선택 필요"}</strong>
                        <small>선택한 이미지를 그대로 VTO에 사용</small>
                      </figcaption>
                    </figure>
                    <figure
                      className="stage-card stage-model featured interactive"
                      role="button"
                      tabIndex={0}
                      aria-label="모델 선택 단계로 이동"
                      onClick={() => openStudioStep("model")}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openStudioStep("model");
                        }
                      }}
                    >
                      <div className="stage-card-head">
                        <span>02</span>
                        <div><b>모델</b><small>화보의 주 피사체</small></div>
                        <i><UiIcon name="arrow-right" /> 변경</i>
                      </div>
                      <div className="stage-card-media">
                        {personMode === "provided_model" ? (
                          <img src={selectedModel.image} alt={selectedModel.name} />
                        ) : personUpload ? (
                          <img src={personUpload.previewUrl} alt="내 모델 사진" />
                        ) : (
                          <div className="stage-placeholder">내 사진을 업로드하세요</div>
                        )}
                      </div>
                      <figcaption>
                        <strong>
                          {personMode === "provided_model"
                            ? selectedModel.name
                            : "내 얼굴 기반"}
                        </strong>
                        <small>
                          {personMode === "provided_model"
                            ? `${selectedModel.gender} · ${selectedModel.detail}`
                            : "선택한 포즈로 새로운 패션 화보 구성"}
                        </small>
                      </figcaption>
                    </figure>
                    <figure
                      className="stage-card stage-scene interactive"
                      role="button"
                      tabIndex={0}
                      aria-label="포즈와 프레이밍 선택 단계로 이동"
                      onClick={() => openStudioStep("style")}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openStudioStep("style");
                        }
                      }}
                    >
                      <div className="stage-card-head">
                        <span>03</span>
                        <div><b>DIRECTION</b><small>포즈와 프레이밍</small></div>
                        <i><UiIcon name="arrow-right" /> 변경</i>
                      </div>
                      <div className="stage-card-media">
                        <img src={selectedPreset.image} alt={selectedPreset.title} />
                      </div>
                      <figcaption>
                        <strong>{selectedPreset.title}</strong>
                        <small>
                          {presetFramingLabel(selectedPreset.framing)} 프레이밍
                        </small>
                      </figcaption>
                    </figure>
                  </div>
                  <div className="stage-note">
                    <b>최종 결과가 아닌 구성 미리보기입니다.</b>
                    <span>
                      선택한 모델·의상·포즈를 바탕으로 완성 이미지 한 장을
                      제공합니다.
                    </span>
                  </div>
                </div>
              )}
              {selected && generatedJobs.length > 0 && (
                <div className="result-strip">
                  <span>최근 생성 결과</span>
                  {generatedJobs.slice(0, 8).map((job) => (
                    <button key={job.id} onClick={() => setViewerJob(job)}>
                      {job.resultUrl && (
                        <img src={job.resultUrl} alt="생성 결과" />
                      )}
                      <i>보기</i>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <aside className="creation-inspector" ref={inspectorRef}>
              <div className="inspector-head">
                <span>
                  단계 {studioSteps[stepIndex]?.number} /{" "}
                  {studioSteps[stepIndex]?.label}
                </span>
                <small>
                  왼쪽 단계 메뉴에서 언제든 이전 설정으로 돌아갈 수 있습니다.
                </small>
              </div>
              <section
                className={`inspector-section ${studioStep === "product" ? "" : "step-hidden"}`}
              >
                <div className="inspector-title">
                  <span>01</span>
                  <div>
                    <b>상품</b>
                    <small>
                      {selected ? `NO. ${selected.productNo}` : "선택 필요"}
                    </small>
                  </div>
                  {selected && (
                    <button
                      onClick={() => {
                        setReshootSource(null);
                        setSelected(null);
                        setSelectedOutfit(null);
                        setOutfitConfirmed(false);
                        window.requestAnimationFrame(() => {
                          resetStudioScroll();
                        });
                      }}
                    >
                      <UiIcon name="refresh" /> 변경
                    </button>
                  )}
                </div>
                {selected && (
                  <div className="chosen-product">
                    <img src={selected.imageUrl ?? ""} alt="" />
                    <strong>{selected.name}</strong>
                  </div>
                )}
                {!selected && (
                  <div className="step-assurance" aria-label="상품 선택 안내">
                    <div>
                      <UiIcon name="search" />
                      <span><b>연동 상품에서 바로 선택</b><small>상품명·번호·카테고리로 빠르게 찾습니다.</small></span>
                    </div>
                    <div>
                      <UiIcon name="image" />
                      <span><b>원본을 확인한 뒤 생성</b><small>대표·상세 이미지 중 실제 의상 원본을 고릅니다.</small></span>
                    </div>
                    <div>
                      <UiIcon name="refresh" />
                      <span><b>적용 전에는 상품 변경 없음</b><small>완성 이미지를 검토한 뒤에만 {platformUi.name}에 반영합니다.</small></span>
                    </div>
                  </div>
                )}
              </section>
              <section
                className={`inspector-section ${studioStep === "outfit" ? "" : "step-hidden"}`}
              >
                <div className="inspector-title">
                  <span>02</span>
                  <div>
                    <b>의상 원본</b>
                    <small>
                      {loadingOutfits
                        ? "의상 이미지 찾는 중"
                        : selectedOutfit
                          ? selectedOutfit.label
                          : "선택 필요"}
                    </small>
                  </div>
                </div>
                {selected && (
                  <>
                    {loadingOutfits ? (
                      <div className="inline-loading">
                        <span className="loader" />
                        상품 이미지와 상세설명에서 의상 원본 후보를 찾고 있어요.
                      </div>
                    ) : (
                      <div className="outfit-source-grid">
                        {outfitCandidates.slice(0, 8).map((candidate) => (
                          <button
                            key={candidate.id}
                            className={
                              selectedOutfit?.id === candidate.id
                                ? "selected"
                                : ""
                            }
                            onClick={() => chooseOutfit(candidate)}
                          >
                            <img src={candidate.url} alt={candidate.label} />
                            <span>
                              {candidate.confidence === "high"
                                ? "추천"
                                : candidate.label}
                            </span>
                          </button>
                        ))}
                        <label
                          className={`upload-tile ${uploadingOutfit ? "busy" : ""}`}
                        >
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(event) => {
                              const file = event.currentTarget.files?.[0];
                              event.currentTarget.value = "";
                              void uploadOutfit(file);
                            }}
                          />
                          <span>＋</span>
                          <b>{uploadingOutfit ? "업로드 중" : "직접 업로드"}</b>
                        </label>
                      </div>
                    )}
                    {selectedOutfit &&
                    (selectedOutfit.warnings.length || garmentView !== "front") ? (
                      <label className="source-confirm">
                        <input
                          type="checkbox"
                          checked={outfitConfirmed}
                          onChange={(event) =>
                            setOutfitConfirmed(event.target.checked)
                          }
                        />
                        <span>
                          <b>
                            {garmentView === "back"
                              ? "이 이미지에서 의상의 실제 뒷면을 확인했습니다."
                              : garmentView === "both"
                                ? "이 이미지에서 의상의 앞면과 뒷면을 모두 확인했습니다."
                                : "선택한 의상 이미지를 직접 확인했습니다."}
                          </b>
                          <small>
                            {garmentView === "front"
                              ? "착용·상세 이미지일 수 있으니 옷 전체가 잘 보이는지 확인하는 권장 항목입니다."
                              : "보이지 않는 면은 정확하게 재현할 수 없습니다. 실제 제품의 해당 면이 선명하게 보여야 합니다."}
                          </small>
                        </span>
                      </label>
                    ) : null}
                    {selectedOutfit && (
                      <>
                        <div className="garment-view-selector">
                          <div>
                            <b>이 사진에서 확인되는 면</b>
                            <small>
                              보이지 않는 옷 구조를 AI가 임의로 만들지 않도록
                              촬영 방향을 제한합니다.
                            </small>
                          </div>
                          <div role="group" aria-label="의상 원본 방향">
                            {(
                              [
                                ["front", "앞면"],
                                ["back", "뒷면"],
                                ["both", "앞·뒤 모두"],
                              ] as const
                            ).map(([value, label]) => (
                              <button
                                key={value}
                                type="button"
                                className={garmentView === value ? "selected" : ""}
                                onClick={() => {
                                  setGarmentView(value);
                                  setOutfitConfirmed(
                                    value === "front" &&
                                      selectedOutfit.confidence === "high",
                                  );
                                }}
                              >
                                <UiIcon name="image" /> {label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="garment-analysis-card source-ready">
                          <div className="garment-analysis-head">
                            <span>의상 원본 준비</span>
                            <b>완료</b>
                          </div>
                          <strong>의상 종류를 별도로 입력하지 않습니다.</strong>
                          <p>선택한 의상 이미지의 형태와 디테일을 VTO 엔진이 직접 참조합니다.</p>
                        </div>
                      </>
                    )}
                  </>
                )}
              </section>
              <section
                className={`inspector-section person-section ${studioStep === "model" ? "" : "step-hidden"}`}
              >
                <div className="inspector-title">
                  <span>03</span>
                  <div>
                    <b>모델 소스</b>
                    <small>
                      {personMode === "provided_model"
                        ? selectedModel.name
                        : "내 얼굴 사진"}
                    </small>
                  </div>
                </div>
                <div className="person-mode-tabs">
                  <button
                    className={personMode === "provided_model" ? "active" : ""}
                    onClick={() => choosePersonMode("provided_model")}
                  >
                    제공 모델
                  </button>
                  {flags?.personalModeEnabled && (
                    <button
                      className={personMode === "personal_face" ? "active" : ""}
                      onClick={() => choosePersonMode("personal_face")}
                    >
                      내 얼굴 사용
                    </button>
                  )}
                </div>
                {personMode === "provided_model" ? (
                  <>
                    <div className="usage-quick">
                      <div className="usage-quick-head">
                        <span><UiIcon name="history" /><b>빠른 모델 선택</b></span>
                        <small>{quickModels.length ? "최근·자주 사용한 순서" : "스튜디오 추천"}</small>
                      </div>
                      <div className="usage-quick-strip quick-model-strip">
                        {modelQuickChoices.map(({ model, reason }) => (
                          <button
                            key={model.id}
                            className={model.id === modelId ? "selected" : ""}
                            onClick={() => chooseQuickModel(model)}
                          >
                            <img
                              src={`/model-thumbnails/${model.id}.jpg`}
                              alt={model.name}
                              onError={(event) => {
                                event.currentTarget.onerror = null;
                                event.currentTarget.src = model.image;
                              }}
                            />
                            <span><b>{model.name}</b><small>{reason}</small></span>
                            {model.id === modelId && <i>선택됨</i>}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mini-tabs">
                      <button
                        className={modelGender === "all" ? "active" : ""}
                        onClick={() => filterModels("all")}
                      >
                        전체
                      </button>
                      <button
                        className={modelGender === "female" ? "active" : ""}
                        onClick={() => filterModels("female")}
                      >
                        여성
                      </button>
                      <button
                        className={modelGender === "male" ? "active" : ""}
                        onClick={() => filterModels("male")}
                      >
                        남성
                      </button>
                    </div>
                    <div className="model-catalog-tools">
                      <input
                        value={modelQuery}
                        onChange={(event) => setModelQuery(event.target.value)}
                        placeholder="모델 이름이나 무드 검색"
                        aria-label="모델 검색"
                      />
                      <div>
                        {modelCastingOptions.map(([value, label]) => (
                          <button
                            key={value}
                            className={modelCasting === value ? "selected" : ""}
                            onClick={() => setModelCasting(value)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="model-source-strip">
                      {visibleModels.map((model) => (
                        <button
                          key={model.id}
                          className={model.id === modelId ? "selected" : ""}
                          onClick={() => setModelId(model.id)}
                        >
                          <img
                            src={`/model-thumbnails/${model.id}.jpg`}
                            alt={model.name}
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = model.image;
                            }}
                          />
                          <span><b>{model.name}</b><small>{model.castingMoodLabel ?? model.detail}</small></span>
                        </button>
                      ))}
                    </div>
                    {visibleModels.length === 0 && (
                      <div className="model-empty">
                        <b>조건에 맞는 모델이 없습니다.</b>
                        <button onClick={() => { setModelQuery(""); setModelCasting("all"); filterModels("all"); }}><UiIcon name="retry" /> 필터 초기화</button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="person-upload-panel">
                    <label className={uploadingPerson ? "busy" : ""}>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(event) => {
                          const file = event.currentTarget.files?.[0];
                          event.currentTarget.value = "";
                          void uploadPerson(file);
                        }}
                      />
                      {personUpload ? (
                        <img
                          src={personUpload.previewUrl}
                          alt="업로드한 내 사진"
                        />
                      ) : (
                        <span>＋</span>
                      )}
                      <b>
                        {uploadingPerson
                          ? "안전하게 업로드 중…"
                          : personUpload
                            ? "다른 사진 선택"
                            : "얼굴이 선명한 사진 업로드"}
                      </b>
                      <small>
                        얼굴 정체성을 참조해 선택한 장면과 포즈로 새 폼피팅 베이스를 만듭니다.
                      </small>
                    </label>
                    {personUpload && (
                      <label className="personal-consent">
                        <input
                          type="checkbox"
                          checked={personalConsent}
                          onChange={(event) =>
                            setPersonalConsent(event.target.checked)
                          }
                        />
                        <span>
                          <b>본인 또는 사용 허락을 받은 성인의 사진입니다.</b>
                          <small>
                            생성 목적에만 사용하며 원본과 결과는 24시간 뒤 자동
                            삭제됩니다.
                          </small>
                        </span>
                      </label>
                    )}
                  </div>
                )}
              </section>
              <section
                className={`inspector-section ${studioStep === "style" ? "" : "step-hidden"}`}
              >
                <div className="inspector-title">
                  <span>04</span>
                  <div>
                    <b>촬영 스타일</b>
                    <small>{selectedPreset.title}</small>
                  </div>
                </div>
                <div className="usage-quick">
                  <div className="usage-quick-head">
                    <span><UiIcon name="history" /><b>빠른 포즈 선택</b></span>
                    <small>{quickPresets.length ? "최근·자주 사용한 순서" : "스튜디오 추천"}</small>
                  </div>
                  <div className="usage-quick-strip quick-preset-strip">
                    {presetQuickChoices.map(({ preset, reason }) => (
                      <button
                        key={preset.code}
                        className={preset.code === presetCode ? "selected" : ""}
                        onClick={() => chooseQuickPreset(preset)}
                      >
                        <img src={preset.image} alt={preset.title} />
                        <span><b>{preset.title}</b><small>{reason}</small></span>
                        {preset.code === presetCode && <i>선택됨</i>}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="preset-tag-status">
                  <b>패션 화보 프리셋</b>
                  <span>{visiblePresets.length}개 표시</span>
                </div>
                <div className="preset-smart-tags" aria-label="상품 적합성 필터">
                  <button
                    className={presetOcclusionSafe ? "selected" : ""}
                    onClick={() => setPresetOcclusionSafe((value) => !value)}
                  >
                    의상 가림 없음
                  </button>
                </div>
                <div className="preset-category-groups">
                  <div>
                    <b>1. 모델별 포즈</b>
                    <div>
                      {(
                        [
                          ["recommended", "선택 모델 추천"],
                          ["all", "전체"],
                          ["feminine", "여성 포즈"],
                          ["masculine", "남성 포즈"],
                          ["universal", "공용 포즈"],
                        ] as const
                      ).map(([value, label]) => (
                        <button
                          key={value}
                          className={presetPresentation === value ? "selected" : ""}
                          onClick={() => setPresetPresentation(value)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <b>2. 화면 범위</b>
                    <div>
                      {presetFramingOptions.map(([value, label]) => (
                        <button
                          key={value}
                          className={presetFraming === value ? "selected" : ""}
                          onClick={() => setPresetFraming(value)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <b>3. 포즈</b>
                    <div>
                      {presetPoseOptions
                        .filter(([value]) => value !== "walking")
                        .map(([value, label]) => (
                          <button
                            key={value}
                            className={presetPoseType === value ? "selected" : ""}
                            onClick={() => setPresetPoseType(value)}
                          >
                            {label}
                          </button>
                        ))}
                    </div>
                  </div>
                  <div>
                    <b>4. 카메라 방향</b>
                    <div>
                      {presetAngleOptions.map(([value, label]) => (
                        <button
                          key={value}
                          className={presetAngle === value ? "selected" : ""}
                          onClick={() => setPresetAngle(value)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="background-tone-picker">
                  <div>
                    <b>5. 배경 밝기</b>
                    <small>조명 방향은 유지하고 스튜디오 배경 농도만 바꿉니다.</small>
                  </div>
                  <div role="group" aria-label="스튜디오 배경 밝기">
                    {(
                      [
                        ["bright", "밝게", "라이트 그레이"],
                        ["balanced", "균형", "웜 그레이"],
                        ["dark", "어둡게", "차콜"],
                      ] as const
                    ).map(([value, label, detail]) => (
                      <button
                        key={value}
                        className={backgroundTone === value ? "selected" : ""}
                        onClick={() => setBackgroundTone(value)}
                      >
                        <i className={`tone-swatch ${value}`} />
                        <span>{label}</span>
                        <small>{detail}</small>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="preset-source-groups">
                  {visiblePresetGroups.map((group) => (
                    <section key={group.framing} className="preset-source-group">
                      <header>
                        <b>{group.label}</b>
                        <span>{group.presets.length}개</span>
                      </header>
                      <div className="preset-source-strip">
                        {group.presets.map((preset) => (
                          <button
                            key={preset.code}
                            className={preset.code === presetCode ? "selected" : ""}
                            onClick={() => setPresetCode(preset.code)}
                          >
                            <img src={preset.image} alt={preset.title} />
                            <span>{preset.title}</span>
                            <small>{presetMetaLine(preset)}</small>
                          </button>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
                {visiblePresets.length === 0 && (
                  <div className="preset-empty">
                    <p>조건에 맞는 프리셋이 없습니다.</p>
                    <button onClick={() => {
                      setPresetFraming("all");
                      setPresetAngle("all");
                      setPresetPoseType("all");
                      setPresetPresentation("recommended");
                      setPresetOcclusionSafe(false);
                    }}><UiIcon name="retry" /> 필터 초기화</button>
                  </div>
                )}
              </section>
              <section
                className={`inspector-section engine-section ${studioStep === "engine" ? "" : "step-hidden"}`}
              >
                <div className="inspector-title">
                  <span>05</span>
                  <div>
                    <b>생성 확인</b>
                    <small>선택 내용과 생성 조건 최종 확인</small>
                  </div>
                </div>
                <div className="generation-summary">
                  <div data-index="01">
                    <span>상품</span>
                    <b>NO. {selected?.productNo}</b>
                    <small>{selected?.name}</small>
                  </div>
                  <div data-index="02">
                    <span>의상</span>
                    <b>{selectedOutfit?.label ?? "의상 원본"}</b>
                    <small>이미지 직접 참조 · 별도 종류 판정 없음</small>
                  </div>
                  <div data-index="03">
                    <span>모델</span>
                    <b>
                      {personMode === "provided_model"
                        ? selectedModel.name
                        : "내 얼굴 기반 AI 화보"}
                    </b>
                  </div>
                  <div data-index="04">
                    <span>촬영</span>
                    <b>{selectedPreset.title}</b>
                    <small>{presetHumanDescription(selectedPreset)}</small>
                  </div>
                  <div data-index="05">
                    <span>배경</span>
                    <b>
                      {backgroundTone === "bright"
                        ? "밝은 라이트 그레이"
                        : backgroundTone === "balanced"
                          ? "균형 잡힌 웜 그레이"
                          : "깊은 차콜"}
                    </b>
                  </div>
                </div>
                <p className="engine-note">
                  선택한 의상·모델·포즈·배경을 기준으로 완성 이미지 한 장을
                  제작합니다. 요청이 접수되면 갤러리에서 진행 상황을 확인할 수 있습니다.
                </p>
              </section>
              <section
                className={`inspector-submit ${studioStep === "engine" ? "" : "step-hidden"}`}
              >
                <div>
                  <span>생성 준비 완료</span>
                  <strong>완성 착용 이미지 1장</strong>
                  <small>
                    {credits
                      ? `현재 ${credits.available.toLocaleString()} · 생성 완료 시 ${credits.generationCredits}크레딧 차감`
                      : "크레딧 잔액을 확인하고 있습니다."}
                  </small>
                </div>
                <button
                  disabled={
                    !selected ||
                    !selectedOutfit ||
                    !outfitConfirmed ||
                    (personMode !== "provided_model" &&
                      (!personUpload || !personalConsent)) ||
                    Boolean(
                      credits &&
                        credits.available < credits.generationCredits,
                    ) ||
                    busy
                  }
                  onClick={generate}
                >
                  <UiIcon name="image" />
                  {busy
                    ? "요청 등록 중…"
                    : personMode !== "provided_model" && !personUpload
                          ? "내 사진을 업로드해 주세요"
                          : personMode !== "provided_model" && !personalConsent
                            ? "사진 사용 동의를 확인해 주세요"
                            : credits &&
                                credits.available < credits.generationCredits
                              ? "크레딧이 부족합니다"
                              : `착용샷 생성하기 · ${credits?.generationCredits ?? 10}크레딧 차감 예정`}
                </button>
              </section>
              <div className="workflow-actions">
                <button
                  className="secondary"
                  disabled={stepIndex === 0}
                  onClick={() =>
                    openStudioStep(studioSteps[stepIndex - 1]?.key ?? "product")
                  }
                >
                  <UiIcon name="arrow-left" /> 이전
                </button>
                {studioStep !== "engine" && (
                  <button
                    className="primary"
                    disabled={!stepComplete(studioStep)}
                    onClick={() =>
                      openStudioStep(
                        studioSteps[stepIndex + 1]?.key ?? "engine",
                      )
                    }
                  >
                    <UiIcon name="arrow-right" />
                    {studioStep === "outfit" && !selectedOutfit
                      ? "의상 이미지 선택 필요"
                      : "다음 단계"}
                  </button>
                )}
              </div>
            </aside>
          </section>
        )}

        {view === "gallery" && (
          <section className="gallery-view">
            <div className="gallery-tabs">
              <button
                className={galleryFilter === "review" ? "active" : ""}
                onClick={() => setGalleryFilter("review")}
              >
                완성 이미지{" "}
                <b>
                  {
                    jobs.filter(
                      (job) =>
                        job.status === "generated" &&
                        !isAppliedStatus(job.applicationStatus),
                    ).length
                  }
                </b>
              </button>
              <button
                className={galleryFilter === "temporary" ? "active" : ""}
                onClick={() => setGalleryFilter("temporary")}
              >
                임시
              </button>
              <button
                className={galleryFilter === "applied" ? "active" : ""}
                onClick={() => setGalleryFilter("applied")}
              >
                적용됨
              </button>
              <button
                className={galleryFilter === "expired" ? "active" : ""}
                onClick={() => setGalleryFilter("expired")}
              >
                만료
              </button>
            </div>
            {galleryJobs.length === 0 ? (
              <div className="gallery-empty">
                <span>◌</span>
                <h2>
                  {jobs.length === 0
                    ? "아직 생성한 결과가 없습니다."
                    : "이 상태의 결과가 없습니다."}
                </h2>
                <button onClick={() => setView("studio")}>
                  <UiIcon name="arrow-left" /> 스튜디오로 이동
                </button>
              </div>
            ) : (
              <div className="gallery-grid">
                {galleryJobs.map((job) => (
                  <article
                    key={job.id}
                    className={`gallery-card ${job.status}`}
                  >
                    <button
                      className="gallery-preview"
                      disabled={!job.resultUrl}
                      aria-label={job.resultUrl ? "완성 이미지 크게 보기" : undefined}
                      onClick={() => {
                        if (job.resultUrl) setViewerJob(job);
                      }}
                    >
                      {job.resultUrl ? (
                        <img src={job.resultUrl} alt="생성 결과" />
                      ) : job.status === "failed" ? (
                        <div className="generation-failure">
                          <span>!</span>
                          <strong>생성하지 못했어요</strong>
                          <small>설정을 불러와 다시 시도할 수 있어요.</small>
                        </div>
                      ) : (
                        <div className="generation-visual">
                          <ProgressOrb progress={job.progress} />
                          <b>{job.stageLabel ?? "이미지를 구성하고 있어요"}</b>
                          <small>완성되면 바로 알려드릴게요.</small>
                        </div>
                      )}
                      <em>
                        {job.simulated
                          ? "미리보기"
                          : job.status === "failed"
                            ? "실패"
                            : "완료"}
                      </em>
                    </button>
                    <div className="gallery-info">
                      <span>상품 번호 {job.productNo}</span>
                      <strong>
                        {job.status === "generated"
                          ? "완성 이미지가 준비됐어요"
                          : job.status === "failed"
                            ? "이번 이미지를 만들지 못했어요"
                            : (job.stageLabel ?? statusText(job.status))}
                      </strong>
                      {["queued", "dispatching", "generating"].includes(job.status) ? (
                        <>
                          <div className="progress-track">
                            <i style={{ width: `${job.progress}%` }} />
                          </div>
                          <small>
                            {job.status === "queued"
                              ? job.queuePosition && job.queuePosition > 1
                                ? `현재 ${job.queuePosition}번째 순서입니다. 화면을 이동해도 대기는 유지됩니다.`
                                : "다음 생성 슬롯이 열리면 자동으로 시작됩니다."
                              : "화면을 이동해도 촬영은 계속 진행됩니다."}
                          </small>
                        </>
                      ) : (
                        <small>
                          {(job.commercePersistenceStatus ?? job.cafe24PersistenceStatus) === "stored"
                            ? platformUi.persistenceLabel
                            : (job.commercePersistenceStatus ?? job.cafe24PersistenceStatus) === "pending" || (job.commercePersistenceStatus ?? job.cafe24PersistenceStatus) === "uploading"
                              ? "쇼핑몰 저장소로 안전하게 옮기고 있어요"
                          : job.expiresAt
                            ? `${timeLeft(job.expiresAt)} 후 삭제`
                            : ""}
                        </small>
                      )}
                      {(job.status === "generated" || job.retryable) && (
                        <div className="gallery-actions">
                          <button
                            disabled={restoringJobId !== null}
                            onClick={() => void restoreJobSetup(job)}
                          >
                            <UiIcon name="refresh" />
                            {restoringJobId === job.id
                              ? "설정 불러오는 중…"
                              : job.status === "generated"
                                ? "이 설정으로 새 촬영"
                                : "설정 복구 후 다시 생성"}
                          </button>
                          {job.status === "generated" && job.resultUrl && (
                            <button
                              className="gallery-detail-action"
                              disabled={
                                !flags?.commerceWriteEnabled ||
                                applyingJobId !== null ||
                                representativeBusy
                              }
                              onClick={() => openDescriptionApplication(job)}
                            >
                              <UiIcon name={isAppliedStatus(job.applicationStatus) ? "align" : "plus"} />
                              {!flags?.commerceWriteEnabled
                                ? `${platformUi.name} 쓰기 권한 준비 중`
                                : isAppliedStatus(job.applicationStatus)
                                  ? "상품 상세 스타일 수정"
                                  : "상품 상세에 추가"}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {view === "tokens" && (
          <section className="token-view">
            <div className="balance-card">
              <span>AVAILABLE BALANCE</span>
              <strong>
                {credits?.available.toLocaleString() ?? "—"}{" "}
                <small>CREDITS</small>
              </strong>
              <p>
                생성 요청 시 {credits?.generationCredits ?? 10}크레딧을 예약하고,
                완성 이미지가 저장된 경우에만 차감합니다. 실패하면 즉시 반환됩니다.
              </p>
              <div className="billing-policy-note">
                {billingPolicy?.purchaseEnabled ? (
                  <>
                    <b>월 구독료 0원 · 선불 크레딧</b>
                    <small>필요한 만큼 크레딧을 구매해 사용합니다.</small>
                  </>
                ) : (
                  <>
                    <b>무료 베타 · 최초 {billingPolicy?.signupCredits ?? 200}크레딧</b>
                    <small>
                      별도 결제 없이 제공된 크레딧으로 최대 {Math.floor((billingPolicy?.signupCredits ?? 200) / (credits?.generationCredits ?? 10))}장의 착용 이미지를 생성할 수 있습니다.
                    </small>
                  </>
                )}
              </div>
            </div>
            {billingPolicy?.purchaseEnabled && commercePlatform === "cafe24" && (
              <div className="credit-purchase-panel">
                <div className="credit-purchase-heading">
                  <span>
                    <small>CAFE24 STORE PAYMENT</small>
                    <h2>필요한 만큼 크레딧을 충전하세요</h2>
                  </span>
                  <p>월 이용료 없이 1회 결제하며, Cafe24 공식 결제창에서 안전하게 처리됩니다.</p>
                </div>
                <div className="credit-package-grid">
                  {creditPackages.map((item) => (
                    <article key={item.id} className="credit-package-card">
                      <span>{item.label}</span>
                      <strong>{item.amountKrw.toLocaleString()}원</strong>
                      <small>
                        착용 이미지 최대 {Math.floor(item.credits / (credits?.generationCredits ?? 10))}장
                      </small>
                      <button
                        type="button"
                        disabled={purchasingPackageId !== null}
                        onClick={() => void purchaseCredits(item.id)}
                      >
                        <UiIcon name="credit" />
                        {purchasingPackageId === item.id
                          ? "결제창 준비 중…"
                          : `${item.label} 구매`}
                      </button>
                    </article>
                  ))}
                </div>
                <p className="credit-refund-note">
                  생성 실패 시 예약 크레딧은 자동 반환됩니다. 구매 크레딧 환불은 Cafe24 결제 내역과 연동해 반영됩니다.
                </p>
              </div>
            )}
            <div className="ledger-panel">
              <div>
                <h2>최근 크레딧 사용 내역</h2>
                <span className="live-pill">
                  예약 {credits?.reserved.toLocaleString() ?? 0}
                </span>
              </div>
              {credits?.items.length ? (
                <div className="credit-ledger-list">
                  {credits.items.map((item) => (
                    <div key={item.id}>
                      <span>
                        <b>
                          {item.type === "purchase"
                            ? "크레딧 구매"
                            : item.type === "refund"
                              ? "크레딧 환불"
                              : "착용 이미지 생성"}
                        </b>
                        <small>
                          {item.type === "purchase"
                            ? "Cafe24 결제 완료"
                            : item.type === "refund"
                              ? "Cafe24 환불 반영"
                              : item.status === "captured"
                            ? "차감 완료"
                            : item.status === "released"
                              ? "실패 · 예약 반환"
                              : "생성 중 · 예약"}
                        </small>
                      </span>
                      <strong className={item.delta < 0 ? "debit" : ""}>
                        {item.delta > 0 ? "+" : item.delta < 0 ? "−" : ""}
                        {Math.abs(item.delta || item.amount).toLocaleString()}
                      </strong>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty">아직 크레딧 사용 내역이 없습니다.</div>
              )}
            </div>
          </section>
        )}

        {view === "settings" && (
          <section className="settings-view">
            <div className="settings-card">
              <span>CONNECTED STORE</span>
              <h2>{commercePlatform === "cafe24" ? `${session.mallId}.cafe24.com` : session.displayName}</h2>
              <p>상품을 읽고, 선택한 완성 이미지만 상세설명에 반영합니다.</p>
              <button
                disabled={busy}
                onClick={() => setConfirmation({ kind: "disconnect" })}
              >
                <UiIcon name="close" /> {platformUi.name} 연결 해제
              </button>
            </div>
            {flags?.personalModeEnabled && (
              <div className="settings-card">
                <span>PERSONAL DATA</span>
                <h2>24시간 임시 처리</h2>
                <p>
                  개인사진과 폼피팅 중간 산출물은 이 독립 Firebase 프로젝트의
                  비공개 저장소에서만 처리하고 24시간 뒤 자동 삭제합니다. 완성
                  이미지는 연결한 쇼핑몰 이미지 저장소에 보관됩니다.
                </p>
              </div>
            )}
          </section>
        )}

      </main>

      {activeJobs.length > 0 && (
        <button className="job-drawer" onClick={() => setView("gallery")}>
          <ProgressOrb progress={activeJobs[0].progress} compact />
          <div>
            <b>{activeJobs[0].stageLabel ?? "착용컷을 만들고 있어요"}</b>
            <small>
              {activeJobs[0].status === "queued"
                ? activeJobs[0].queuePosition && activeJobs[0].queuePosition > 1
                  ? `현재 ${activeJobs[0].queuePosition}번째 순서 · 차례가 되면 자동으로 시작됩니다.`
                  : "다음 순서 · 생성 슬롯이 열리면 자동으로 시작됩니다."
                : "완성되면 바로 알려드릴게요."}
            </small>
          </div>
          <i style={{ width: `${activeJobs[0].progress}%` }} />
          <em><UiIcon name="arrow-right" /> 열기</em>
        </button>
      )}

      <div className="toast-stack" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast-card ${toast.tone}`} role="status">
            <span className="toast-symbol" aria-hidden>
              {toast.tone === "success"
                ? "✓"
                : toast.tone === "error"
                  ? "!"
                  : toast.tone === "info"
                    ? "i"
                    : ""}
            </span>
            <div>
              <b>{toast.title}</b>
              <small>{toast.message}</small>
            </div>
            {toast.actionHref && (
              <a href={toast.actionHref} target="_blank" rel="noreferrer">
                <UiIcon name="external" /> {toast.actionLabel}
              </a>
            )}
            {toast.actionView && (
              <button
                type="button"
                className="toast-action"
                onClick={() => {
                  setView(toast.actionView!);
                  setToasts((current) =>
                    current.filter((item) => item.id !== toast.id),
                  );
                }}
              >
                <UiIcon name="arrow-right" /> {toast.actionLabel}
              </button>
            )}
            {toast.tone !== "working" && (
              <button
                type="button"
                aria-label="알림 닫기"
                onClick={() =>
                  setToasts((current) =>
                    current.filter((item) => item.id !== toast.id),
                  )
                }
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      {viewerJob?.resultUrl && (
        <div
          className="result-modal"
          role="dialog"
          aria-modal="true"
          aria-label="완성 이미지"
          onClick={(event) => {
            if (event.target === event.currentTarget) setViewerJob(null);
          }}
        >
          <div className="result-review engine-review">
            <div className="review-toolbar">
              <div className="review-heading">
                <span>RESULT REVIEW</span>
                <strong>완성 이미지</strong>
                <small>원본과 비교한 뒤 필요한 작업을 선택하세요.</small>
              </div>
              <div>
                <button
                  className="review-close"
                  onClick={() => setViewerJob(null)}
                >
                  <UiIcon name="close" /> 닫기
                </button>
              </div>
            </div>
            <div
              className={`compare-stage ${viewerJob.outfit?.imageUrl ? "" : "result-only"}`}
            >
              {viewerJob.outfit?.imageUrl && (
                <figure className="source-figure">
                  <img src={viewerJob.outfit.imageUrl} alt="선택한 의상 원본" />
                  <figcaption>
                    <b>의상 원본</b>
                    <span>색상·실루엣·디테일을 결과와 비교해 주세요.</span>
                  </figcaption>
                </figure>
              )}
              <figure>
                <img src={viewerJob.resultUrl} alt="생성된 착용샷" />
                <figcaption>
                  <b>선택한 모델과 포즈로 만든 착용 이미지</b>
                  <span>
                    이미지를 확인한 뒤 상품 상세에 바로 추가할 수 있습니다.
                  </span>
                </figcaption>
              </figure>
            </div>
            <div className="review-footer">
              <div className="review-product-meta">
                <span>상품 번호 {viewerJob.productNo}</span>
                <strong>
                  {jobModelName(viewerJob, models)} · {jobPresetName(viewerJob, presets)}
                </strong>
                <small>
                  {(viewerJob.commercePersistenceStatus ?? viewerJob.cafe24PersistenceStatus) === "stored"
                    ? `${platformUi.name} 저장소에 계속 보관됩니다.`
                    : (viewerJob.commercePersistenceStatus ?? viewerJob.cafe24PersistenceStatus) === "pending" || (viewerJob.commercePersistenceStatus ?? viewerJob.cafe24PersistenceStatus) === "uploading"
                      ? `Firebase 백업을 유지하며 ${platformUi.name} 저장소 업로드를 재시도합니다.`
                  : viewerJob.expiresAt
                    ? `${timeLeft(viewerJob.expiresAt)} 후 원본과 결과 자동 삭제`
                    : ""}
                </small>
                <a
                  className="review-product-link"
                  href={productPageUrl(viewerJob.productNo)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <UiIcon name="external" /> {platformUi.name} 상품 보기
                </a>
              </div>
              <div className="review-actions">
                <div className="review-secondary-actions">
                  <button
                    className="review-action review-action-secondary"
                    disabled={restoringJobId !== null}
                    onClick={() => void restoreJobSetup(viewerJob)}
                  >
                    <UiIcon name="refresh" />
                    {restoringJobId === viewerJob.id
                      ? "설정 불러오는 중…"
                      : "이 설정으로 새 촬영"}
                  </button>
                  <button
                    className="review-action review-action-secondary representative-action"
                    disabled={
                      !flags?.commerceWriteEnabled ||
                      representativeBusy ||
                      applyingJobId !== null
                    }
                    onClick={() => void openRepresentative(viewerJob)}
                  >
                    <UiIcon name="image" /> 대표이미지로 사용
                  </button>
                </div>
                <div className="review-primary-actions">
                  {viewerApplied ? (
                    <button
                      className="review-action review-action-primary"
                      disabled={applyingJobId !== null || representativeBusy}
                      onClick={() => openDescriptionApplication(viewerJob)}
                    >
                      <UiIcon name="align" /> 스타일·캡션 수정
                    </button>
                  ) : (
                    <button
                      className="review-action review-action-primary"
                      disabled={
                        !flags?.commerceWriteEnabled ||
                        applyingJobId !== null ||
                        representativeBusy
                      }
                      onClick={() => openDescriptionApplication(viewerJob)}
                    >
                      <UiIcon name="plus" />
                      {!flags?.commerceWriteEnabled
                        ? `${platformUi.name} 쓰기 권한 준비 중`
                        : applyingJobId === viewerJob.id
                          ? "적용 중…"
                          : "상품 상세에 추가"}
                    </button>
                  )}
                </div>
                <details className="review-more-actions">
                  <summary aria-label="추가 작업 열기">
                    <span aria-hidden="true">•••</span> 더보기
                  </summary>
                  <div>
                    <a href={`/api/jobs/${encodeURIComponent(viewerJob.id)}/download`} download>
                      <UiIcon name="download" /> 이미지 다운로드
                    </a>
                    <a href={viewerJob.resultUrl} target="_blank" rel="noreferrer">
                      <UiIcon name="external" /> 이미지 새 탭에서 보기
                    </a>
                    {viewerApplied && (
                      <button
                        className="review-danger-action"
                        disabled={applyingJobId !== null || representativeBusy}
                        onClick={() =>
                          setConfirmation({ kind: "remove", job: viewerJob })
                        }
                      >
                        <UiIcon name="trash" />
                        {applyingJobId === viewerJob.id
                          ? "처리 중…"
                          : "상품 상세에서 제거"}
                      </button>
                    )}
                  </div>
                </details>
              </div>
            </div>
          </div>
        </div>
      )}
      {confirmation && (
        <div
          className="confirmation-modal"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="confirmation-title"
          onClick={(event) => {
            if (event.target === event.currentTarget) setConfirmation(null);
          }}
        >
          <section className="confirmation-panel">
            <span>CONFIRM ACTION</span>
            <h2 id="confirmation-title">
              {confirmation.kind === "remove"
                ? "상품 상세에서 이미지를 제거할까요?"
                : `${platformUi.name} 연결을 해제할까요?`}
            </h2>
            <p>
              {confirmation.kind === "remove"
                ? "스냅캐스트가 추가한 이미지 블록만 제거하며, 사장님이 작성한 나머지 상품 설명은 그대로 유지됩니다."
                : "생성 기록과 임시 스냅샷이 삭제되며, 다시 연결하기 전까지 상품을 불러올 수 없습니다."}
            </p>
            <div>
              <button type="button" onClick={() => setConfirmation(null)}>
                <UiIcon name="close" /> 취소
              </button>
              <button
                type="button"
                className="danger"
                onClick={() =>
                  confirmation.kind === "remove"
                    ? void remove(confirmation.job)
                    : void disconnect()
                }
              >
                <UiIcon name={confirmation.kind === "remove" ? "trash" : "close"} />
                {confirmation.kind === "remove" ? "이미지 제거" : "연결 해제"}
              </button>
            </div>
          </section>
        </div>
      )}
      {descriptionJob?.resultUrl && (
        <div
          className="description-application-modal"
          role="dialog"
          aria-modal="true"
          aria-label="상품 상세 이미지 스타일 설정"
          onClick={(event) => {
            if (
              event.target === event.currentTarget &&
              applyingJobId !== descriptionJob.id
            )
              setDescriptionJob(null);
          }}
        >
          <div className="description-application-panel">
            <header>
              <div>
                <span>PRODUCT DETAIL COMPOSER</span>
                <h2>상품 상세에 맞게 다듬기</h2>
                <p>스타일과 메시지를 미리 확인한 뒤 상점에 반영합니다.</p>
              </div>
              <button
                type="button"
                disabled={applyingJobId === descriptionJob.id}
                onClick={() => setDescriptionJob(null)}
              >
                <UiIcon name="close" /> 닫기
              </button>
            </header>
            <div className="description-composer-body">
              <section className="description-preview-area">
                <div className="description-preview-toolbar">
                  <span>LIVE PREVIEW</span>
                  <small>상품 본문 폭에서 보이는 구성을 미리 보여드려요.</small>
                </div>
                <div className={`description-preview-canvas layout-${descriptionOptions.layout}`}>
                  <figure>
                    <img src={descriptionJob.resultUrl} alt="상품 상세 삽입 미리보기" />
                    {(descriptionOptions.captionTitle || descriptionOptions.captionBody) && (
                      <figcaption>
                        {descriptionOptions.captionTitle && <strong>{descriptionOptions.captionTitle}</strong>}
                        {descriptionOptions.captionBody && <span>{descriptionOptions.captionBody}</span>}
                      </figcaption>
                    )}
                  </figure>
                </div>
                <div className="description-preview-context">
                  <i />
                  <span>기존 상품 설명은 그대로 유지되며, 이 이미지 블록만 다시 편집하거나 제거할 수 있어요.</span>
                  <i />
                </div>
              </section>
              <aside className="description-composer-controls">
                <section>
                  <div className="composer-section-heading">
                    <span>01</span>
                    <div><b>표현 스타일</b><small>상품 상세 구성에 어울리는 모양을 고르세요.</small></div>
                  </div>
                  <div className="description-layout-grid">
                    {DESCRIPTION_LAYOUTS.map((layout) => (
                      <button
                        type="button"
                        key={layout.key}
                        className={descriptionOptions.layout === layout.key ? "selected" : ""}
                        disabled={applyingJobId === descriptionJob.id}
                        onClick={() => setDescriptionOptions((current) => ({ ...current, layout: layout.key }))}
                      >
                        <i className={`layout-swatch ${layout.key}`}><span /></i>
                        <b>{layout.name}</b>
                        <small>{layout.description}</small>
                      </button>
                    ))}
                  </div>
                </section>
                <section>
                  <div className="composer-section-heading">
                    <span>02</span>
                    <div><b>삽입 위치</b><small>기존 상품 설명은 손대지 않아요.</small></div>
                  </div>
                  <div className="description-placement-control">
                    <button type="button" className={descriptionOptions.placement === "top" ? "selected" : ""} disabled={applyingJobId === descriptionJob.id} onClick={() => setDescriptionOptions((current) => ({ ...current, placement: "top" }))}>
                      <UiIcon name="arrow-left" /> 상세설명 맨 위
                    </button>
                    <button type="button" className={descriptionOptions.placement === "bottom" ? "selected" : ""} disabled={applyingJobId === descriptionJob.id} onClick={() => setDescriptionOptions((current) => ({ ...current, placement: "bottom" }))}>
                      <UiIcon name="arrow-right" /> 상세설명 맨 아래
                    </button>
                  </div>
                </section>
                <section>
                  <div className="composer-section-heading">
                    <span>03</span>
                    <div><b>캡션</b><small>필요할 때만 작성하세요. 비워두면 이미지만 들어갑니다.</small></div>
                  </div>
                  <label className="description-field">
                    <span>제목 <small>{descriptionOptions.captionTitle?.length ?? 0}/80</small></span>
                    <input
                      value={descriptionOptions.captionTitle ?? ""}
                      maxLength={80}
                      disabled={applyingJobId === descriptionJob.id}
                      placeholder="예: SUMMER EDITORIAL"
                      onChange={(event) => setDescriptionOptions((current) => ({ ...current, captionTitle: event.target.value }))}
                    />
                  </label>
                  <label className="description-field">
                    <span>설명 <small>{descriptionOptions.captionBody?.length ?? 0}/300</small></span>
                    <textarea
                      value={descriptionOptions.captionBody ?? ""}
                      maxLength={300}
                      rows={4}
                      disabled={applyingJobId === descriptionJob.id}
                      placeholder="예: 여름의 선명한 실루엣을 담은 룩"
                      onChange={(event) => setDescriptionOptions((current) => ({ ...current, captionBody: event.target.value }))}
                    />
                  </label>
                </section>
              </aside>
            </div>
            <footer>
              <div>
                <b>{descriptionJob.productNo}번 상품에 반영합니다.</b>
                <span>{isAppliedStatus(descriptionJob.applicationStatus) ? "기존 스냅캐스트 블록을 중복 없이 수정합니다." : "반영 전에는 상점에 변경이 없습니다."}</span>
              </div>
              <button type="button" disabled={applyingJobId !== null} onClick={() => void apply(descriptionJob, descriptionOptions)}>
                {applyingJobId === descriptionJob.id ? <><i className="button-spinner" /> 상품 상세에 반영 중…</> : <><UiIcon name="plus" /> {isAppliedStatus(descriptionJob.applicationStatus) ? "스타일과 캡션 저장" : "상품 상세에 추가"}</>}
              </button>
            </footer>
          </div>
        </div>
      )}
      {representativeJob && (
        <div className="representative-modal" role="dialog" aria-modal="true" aria-label="대표이미지 교체">
          <div className="representative-panel">
            <header>
              <div>
                <span>PRODUCT IMAGE APPLICATION</span>
                <h2>대표이미지로 사용</h2>
                <p>현재 상품 이미지 비율에 맞춘 모습을 확인한 뒤 교체하세요.</p>
              </div>
              <button disabled={representativeBusy} onClick={() => {
                setRepresentativeJob(null);
                setRepresentativePreview(null);
              }}><UiIcon name="close" /> 닫기</button>
            </header>
            {representativeBusy && !representativePreview ? (
              <div className="representative-body representative-loading">
                <i />
                <b>현재 상품 이미지 구성을 불러오고 있어요.</b>
                <span>상세·목록·작은목록·썸네일 비율을 확인합니다.</span>
              </div>
            ) : representativePreview ? (
              <>
                <div className="representative-body">
                  <div className="representative-mode">
                  <button className={representativeMode === "contain" ? "selected" : ""} onClick={() => setRepresentativeMode("contain")} disabled={representativeBusy || representativeApplied}>
                    <b>전체 이미지 유지</b><span>잘리지 않도록 여백을 더합니다.</span>
                  </button>
                  <button className={representativeMode === "cover" ? "selected" : ""} onClick={() => setRepresentativeMode("cover")} disabled={representativeBusy || representativeApplied}>
                    <b>화면 가득 채우기</b><span>중앙을 기준으로 가장자리를 자릅니다.</span>
                  </button>
                  </div>
                  <div className="representative-preview-grid">
                  {representativePreview.slots.map((slot) => {
                    const ratio = slot.width && slot.height ? `${slot.width} / ${slot.height}` : "4 / 5";
                    const label = {
                      detail_image: "상품 상세 대표",
                      list_image: "상품 목록",
                      small_image: "상세 썸네일",
                      tiny_image: "작은 목록",
                    }[slot.field];
                    return (
                      <figure key={slot.field}>
                        <div style={{ aspectRatio: ratio }}>
                          <img src={representativePreview.resultUrl} alt={`${label} 교체 미리보기`} style={{ objectFit: representativeMode === "contain" ? "contain" : "cover" }} />
                        </div>
                        <figcaption><b>{label}</b><span>{slot.width && slot.height ? `${slot.width} × ${slot.height}` : "비율 정보 없음"}</span></figcaption>
                      </figure>
                    );
                  })}
                  </div>
                  <div className="representative-safety">
                    <b>교체 전 이미지는 30일 동안 안전하게 보관됩니다.</b>
                    <span>다른 곳에서 상품 이미지가 수정되면 덮어쓰지 않고 작업을 중단합니다.</span>
                  </div>
                </div>
                <footer>
                  {representativeApplied ? (
                    <>
                      <div><b>대표이미지 교체가 완료됐어요.</b><span>상품 페이지에서 실제 노출을 확인했습니다.</span></div>
                      <button className="rollback" disabled={representativeBusy} onClick={() => void rollbackRepresentative()}><UiIcon name="retry" /> {representativeBusy ? "복원 중…" : "이전 대표이미지로 되돌리기"}</button>
                    </>
                  ) : (
                    <>
                      <div><b>상세·목록·썸네일 이미지가 함께 변경됩니다.</b><span>백업과 실제 반영 확인을 자동으로 진행합니다.</span></div>
                      <button disabled={representativeBusy} onClick={() => void confirmRepresentative()}><UiIcon name="image" /> {representativeBusy ? "백업하고 반영 확인 중…" : "대표이미지 전체 교체"}</button>
                    </>
                  )}
                </footer>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function Login({ error }: { error: string | null }) {
  const [mallId, setMallId] = useState("");
  const [connectionOpen, setConnectionOpen] = useState(Boolean(error));
  const local =
    location.hostname === "127.0.0.1" || location.hostname === "localhost";
  useEffect(() => {
    if (!connectionOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setConnectionOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [connectionOpen]);
  return (
    <div className="login">
      <div className="login-editorial">
        <div className="login-top">
          <a className="landing-nav-brand" href="/" aria-label={`Snapcast ${platformUi.edition} 홈`}>
            <img src="/brand/snapcast-logo-white.png" alt="Snapcast" />
            <i aria-hidden="true" />
            <b>{platformUi.edition}</b>
          </a>
          <small>{platformUi.name} 상품 연동 스튜디오</small>
        </div>
        <div className="landing-showcase">
          <figure className="landing-result">
            <img src="/brand/landing-campaign-v2.jpg" alt="스냅캐스트 패션 에디토리얼 캠페인" />
            <figcaption>
              <span>SNAPCAST FASHION EDITORIAL</span>
              <b>CAMPAIGN 2026</b>
            </figcaption>
          </figure>
          <aside className="landing-story">
            <div className="landing-message">
              <span>FASHION COMMERCE, REIMAGINED</span>
              <strong>촬영의 한계를 넘어,<br />브랜드의 장면을 만드세요.</strong>
              <p>상품 사진 한 장에서 시작하는 패션 에디토리얼.<br />모델과 포즈, 무드를 고르고 {platformUi.name}에 바로 적용하세요.</p>
            </div>
          </aside>
        </div>
        <div className="landing-proof" aria-label="주요 기능">
          <span><b>60+</b> SELECTED MODELS</span>
          <span><b>120+</b> EDITORIAL POSES</span>
          <span><b>DIRECT</b> {platformUi.publishingLabel}</span>
        </div>
      </div>
      <div className="login-panel">
        <a className="brand login-brand" href="/" aria-label={`Snapcast ${platformUi.edition} 홈`}>
          <img src="/brand/snapcast-logo-white.png" alt="Snapcast" />
          <span className="brand-divider" aria-hidden="true">|</span>
          <span className="brand-edition">{platformUi.edition}</span>
        </a>
        <div className="login-copy">
          <span className="eyebrow">AI FITTING WORKSPACE</span>
          <h1>
            모델·스튜디오 섭외 없이,
            <br />
            일관된 패션 콘텐츠를.
          </h1>
          <p>
            상품 사진과 원하는 모델, 포즈와 무드를 선택하세요.
            <br />
            반복되는 촬영 없이 브랜드 톤을 유지한 착용컷을 만듭니다.
          </p>
          <div className="login-features">
            <span>01 상품 자동 연동</span>
            <span>02 모델 선택</span>
            <span>03 촬영 무드</span>
          </div>
        </div>
        {commercePlatform === "cafe24" ? <>
          <section className="connection-launch" aria-labelledby="connection-launch-title">
            <div>
              <span>CAFE24 CONNECTION</span>
              <h2 id="connection-launch-title">내 쇼핑몰 상품으로 바로 시작하세요.</h2>
              <p>몰 ID만 확인하면 상품을 안전하게 불러오고, 완성된 착용컷을 다시 상품에 적용할 수 있습니다.</p>
            </div>
            <button className="connection-launch-button" onClick={() => setConnectionOpen(true)}>
              <UiIcon name="arrow-right" /> Cafe24 연결하기
            </button>
          </section>
          {connectionOpen && <div className="connection-modal" role="presentation" onMouseDown={(event) => {
            if (event.target === event.currentTarget) setConnectionOpen(false);
          }}>
            <section className="connection-dialog" role="dialog" aria-modal="true" aria-labelledby="connection-dialog-title">
              <header>
                <div>
                  <span>CAFE24 CONNECTION</span>
                  <h2 id="connection-dialog-title">쇼핑몰 연결</h2>
                </div>
                <button type="button" aria-label="연결 창 닫기" onClick={() => setConnectionOpen(false)}><UiIcon name="close" /></button>
              </header>
              <p className="connection-dialog-copy">Cafe24 관리자에서 사용하는 몰 ID를 입력하세요.</p>
              <label>
                {platformUi.accountLabel}
                <input
                  autoFocus
                  autoComplete="off"
                  value={mallId}
                  onChange={(event) =>
                    setMallId(
                      event.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""),
                    )
                  }
                  placeholder="예: myshop"
                />
              </label>
              {error && <p className="login-error">{friendlyError(error)}</p>}
              <a
                className={`login-button ${!mallId ? "disabled" : ""}`}
                aria-disabled={!mallId}
                href={mallId ? `/api/oauth/start?mall_id=${encodeURIComponent(mallId)}` : undefined}
              >
                Cafe24 연결하고 시작하기 <UiIcon name="arrow-right" />
              </a>
              {local && (
                <button className="dev-login" disabled={!mallId} onClick={() => api.devLogin(mallId).then(() => location.reload())}>
                  <UiIcon name="arrow-right" /> 로컬 검증 세션으로 입장
                </button>
              )}
              <small className="legal"><b>상품 조회·쓰기 권한 사용</b> · 최종 적용 전에는 상품을 변경하지 않습니다.</small>
            </section>
          </div>}
        </> : <div className="connect-box smartstore-connect-box">
          <span className="connect-box-kicker">SMARTSTORE CONNECTION</span>
          <h2>스마트스토어에서 연결하세요.</h2>
          <p>솔루션마켓에서 Snapcast를 실행하면 판매자 계정과 상품을 안전하게 연결합니다.</p>
          <a
            className="login-button"
            href="https://sell.smartstore.naver.com/"
          >
            <UiIcon name="arrow-right" /> 스마트스토어로 이동
          </a>
          {error && <p className="login-error">{friendlyError(error)}</p>}
          <small className="legal">
            <b>상품 조회·쓰기 권한 사용</b> · 생성 결과는 최종 적용 확인 전까지
            상품을 변경하지 않습니다.
          </small>
        </div>}
      </div>
      {commercePlatform === "naver" && <footer className="naver-legal-footer">
        <strong>상호 이상단 주식회사</strong>
        <span>사업자등록번호 879-81-03741</span>
        <a href="mailto:support@esangdan.com">support@esangdan.com</a>
        <span>© 2026 ESANGDAN</span>
      </footer>}
    </div>
  );
}

function Icon({ name }: { name: string }) {
  return <UiIcon name={name === "spark" ? "spark" : name === "clock" ? "clock" : "settings"} />;
}
function timeLeft(value: string) {
  const milliseconds = Math.max(0, new Date(value).getTime() - Date.now());
  const hours = Math.ceil(milliseconds / 3_600_000);
  return hours >= 24 ? `${Math.ceil(hours / 24)}일` : `${hours}시간`;
}
function modelGenderCode(model: CatalogModel) {
  return model.genderCode === "male" || model.gender === "male" || model.gender.startsWith("남성")
    ? "male"
    : "female";
}

function presetMetaLine(preset: CatalogPreset) {
  const framing = presetFramingLabel(preset.framing);
  const parts = [
    preset.title.includes(framing) ? null : framing,
    preset.occlusion.includes("none") ? "가림 없음" : null,
    preset.posePresentation === "feminine"
      ? "여성 포즈"
      : preset.posePresentation === "masculine"
        ? "남성 포즈"
        : "공용 포즈",
  ];
  return parts.filter(Boolean).join(" · ");
}
function presetHumanDescription(preset: CatalogPreset) {
  const framing: Record<string, string> = {
    "full-body": "전신",
    "three-quarter": "7부 · 무릎",
    "upper-body": "상반신 · 허리",
    "lower-body": "하반신 · 허리 아래",
    profile: "프로필 · 가슴",
  };
  const angle: Record<string, string> = {
    front: "정면",
    "three-quarter-left": "좌 3/4",
    "three-quarter-right": "우 3/4",
    "side-left": "좌측면",
    "side-right": "우측면",
    back: "후면",
  };
  const pose: Record<string, string> = {
    standing: "서있는 자세",
  };
  return [
    framing[preset.framing] ?? "선택 프레이밍",
    angle[preset.viewAngle] ?? "선택 시점",
    pose[preset.poseType] ?? "정적인 포즈",
  ].join(" · ");
}

function presetFramingLabel(framing: string) {
  const labels: Record<string, string> = {
    "full-body": "전신",
    "three-quarter": "7부 · 무릎",
    "upper-body": "상반신 · 허리",
    "lower-body": "하반신 · 허리 아래",
    profile: "프로필 · 가슴",
  };
  return labels[framing] ?? "선택 범위";
}
function jobModelName(job: Job, models: CatalogModel[]) {
  return (
    models.find((model) => model.id === job.modelKey)?.name ?? "모델 정보 없음"
  );
}
function jobPresetName(job: Job, presets: CatalogPreset[]) {
  return (
    presets.find((preset) => preset.code === job.presetCode)?.title ??
    "스타일 정보 없음"
  );
}
function statusText(status: string) {
  return (
    (
      {
        queued: "대기 중",
        dispatching: "생성 시작 중",
        generating: "생성 중",
        generated: "생성 완료",
        failed: "생성 실패",
        expired: "만료됨",
      } as Record<string, string>
    )[status] ?? status
  );
}
function friendlyError(code: string) {
  return (
    (
      {
        MALL_NOT_ALLOWED: "현재 허용된 테스트 쇼핑몰이 아닙니다.",
        PRODUCT_IMAGE_NOT_OWNED_BY_PRODUCT:
          "선택한 이미지가 해당 상품의 이미지인지 확인할 수 없습니다.",
        BILLING_DISABLED: "과금 기능이 아직 활성화되지 않았습니다.",
        INSUFFICIENT_CREDITS:
          "크레딧이 부족합니다. 크레딧을 충전한 뒤 다시 시도해 주세요.",
        CREDITS_LOAD_FAILED:
          "크레딧 잔액을 불러오지 못했습니다. 잠시 후 다시 확인해 주세요.",
        PERSONAL_MODE_DISABLED:
          "내 사진 모드는 개인정보 안전 검토 후 열립니다.",
        IMAGE_FILE_REQUIRED:
          "이미지 파일이 비어 있거나 손상되었습니다. 다른 파일을 선택해 주세요.",
        UNSUPPORTED_IMAGE_FORMAT:
          "JPG, PNG 또는 WebP 이미지만 올릴 수 있습니다.",
        IMAGE_TOO_LARGE: "이미지는 10MB 이하로 올려 주세요.",
        IMAGE_RESOLUTION_TOO_LOW:
          "이미지 해상도가 너무 낮습니다. 긴 변 700px 이상의 이미지를 선택해 주세요.",
        INVALID_IMAGE:
          "이미지를 읽을 수 없습니다. 다른 파일로 다시 시도해 주세요.",
        DISCONNECT_REQUIRES_REMOVAL:
          "상품 상세에 적용된 이미지를 생성 내역에서 모두 제거한 뒤 연결을 해제해 주세요.",
        CONNECTION_OWNER_REQUIRED:
          "처음 앱을 연결한 관리자만 연결을 해제할 수 있습니다.",
        MANAGED_BLOCK_NOT_FOUND:
          "상품 상세에서 스냅캐스트 이미지 블록을 찾지 못했습니다. 고객지원에서 확인이 필요합니다.",
        INTERNAL_ERROR:
          "서비스에서 요청을 처리하지 못했습니다. 선택한 내용은 유지되니 잠시 후 다시 시도해 주세요.",
        GENERATION_REQUEST_FAILED:
          "생성 요청을 등록하지 못했습니다. 잠시 후 다시 시도해 주세요.",
        QUALITY_GATE_FAILED:
          "이전 생성 방식의 작업이 완료되지 않았습니다. 선택한 설정을 불러와 다시 시도해 주세요.",
        PERSONAL_CONSENT_REQUIRED:
          "내 사진 사용 동의를 확인해 주세요.",
        PRODUCTS_LOAD_FAILED:
          "상품 목록을 불러오지 못했습니다. 잠시 후 새로고침해 주세요.",
        OUTFIT_CANDIDATE_NOT_OWNED_BY_PRODUCT:
          "선택한 의상 원본이 현재 상품 이미지와 일치하지 않습니다. 의상 원본을 다시 선택해 주세요.",
        RESHOOT_OUTFIT_NOT_FOUND:
          "이전 촬영의 의상 원본이 만료되었거나 삭제되었습니다. 의상 원본을 다시 선택해 주세요.",
        RESHOOT_SOURCE_UNAVAILABLE:
          "이전 촬영의 의상 원본을 다시 사용할 수 없습니다. 의상 이미지를 다시 선택해 주세요.",
        RESHOOT_RESTORE_FAILED:
          "이전 촬영 설정을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.",
        REAR_GARMENT_REFERENCE_REQUIRED:
          "후면 촬영에는 뒷면이 확인되는 의상 원본이 필요합니다. 의상 원본 단계에서 뒷면 사진을 선택하거나 업로드해 주세요.",
        FRONT_GARMENT_REFERENCE_REQUIRED:
          "정면 촬영에는 앞면이 확인되는 의상 원본이 필요합니다. 의상 원본 단계에서 앞면 사진을 선택하거나 업로드해 주세요.",
        PRESET_NOT_RELEASED:
          "이 촬영 프리셋은 품질 검증 중이라 현재 사용할 수 없습니다. 목록에서 다른 포즈를 선택해 주세요.",
        JOBS_LOAD_FAILED:
          "생성 내역을 불러오지 못했습니다. 잠시 후 다시 확인해 주세요.",
        PRODUCT_IMAGES_CHANGED:
          "다른 화면에서 상품 이미지가 변경되었습니다. 최신 이미지를 다시 불러온 뒤 확인해 주세요.",
        PRODUCT_DESCRIPTION_CHANGED:
          "다른 화면에서 상품 상세설명이 변경되어 반영을 멈춰습니다. 새로고침한 뒤 최신 설명에 다시 적용해 주세요.",
        PRODUCT_REPRESENTATIVE_IMAGE_MISSING:
          `현재 상품의 대표이미지를 찾지 못했습니다. ${platformUi.name} 상품 관리자에서 대표이미지를 먼저 확인해 주세요.`,
        CAFE24_REPRESENTATIVE_VERIFICATION_FAILED:
          `${platformUi.name} 반영 여부를 확인하지 못했습니다. 상품 페이지를 확인한 뒤 다시 시도해 주세요.`,
        CAFE24_ROLLBACK_VERIFICATION_FAILED:
          "이전 이미지 복원 여부를 확인하지 못했습니다. 상품 관리자에서 대표이미지를 확인해 주세요.",
        SNAPSHOT_EXPIRED:
          "이전 대표이미지의 안전 보관기간이 지나 자동 복원할 수 없습니다.",
        THUMBNAIL_PREVIEW_FAILED:
          "대표이미지 미리보기를 준비하지 못했습니다. 잠시 후 다시 시도해 주세요.",
        THUMBNAIL_APPLY_FAILED:
          "대표이미지를 교체하지 못했습니다. 기존 이미지는 유지됩니다.",
        THUMBNAIL_ROLLBACK_FAILED:
          "이전 대표이미지로 되돌리지 못했습니다. 상품 관리자에서 현재 이미지를 확인해 주세요.",
        OAUTH_INVALID_SCOPE:
          `${platformUi.name} 앱 권한 설정이 일치하지 않습니다. 앱 권한을 확인한 뒤 다시 연결해 주세요.`,
        OAUTH_ACCESS_DENIED:
          `${platformUi.name} 권한 승인이 취소되었습니다. 연결하려면 권한 확인 화면에서 동의해 주세요.`,
        OAUTH_STATE_REJECTED:
          "연결 확인 정보가 일치하지 않습니다. 열려 있는 연결 창을 닫고 스튜디오 시작하기를 한 번만 눌러 다시 연결해 주세요.",
        OAUTH_STATE_EXPIRED:
          "연결 확인 시간이 지났습니다. 스튜디오 시작하기를 눌러 다시 연결해 주세요.",
        OAUTH_CAFE24_TOKEN_REQUEST_FAILED:
          `${platformUi.name} 연결 승인을 완료하지 못했습니다. 잠시 후 다시 연결해 주세요.`,
        OAUTH_INVALID_REQUEST:
          `${platformUi.name}에서 돌아온 연결 정보가 올바르지 않습니다. 다시 연결해 주세요.`,
        OAUTH_INTERNAL_ERROR:
          `${platformUi.name} 연결을 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.`,
        NAVER_TOKEN_REQUEST_FAILED:
          "스마트스토어 인증 토큰을 발급하지 못했습니다. 솔루션 연결 상태를 확인한 뒤 다시 실행해 주세요.",
        NAVER_COMMERCE_API_FAILED:
          "스마트스토어 상품 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.",
        COMMERCE_RESULT_NOT_STORED:
          `${platformUi.name} 저장을 완료하지 못했습니다. 선택 내용은 유지되니 잠시 후 다시 시도해 주세요.`,
        COMMERCE_RESULT_SAVE_RETRY_FAILED:
          `${platformUi.name} 저장을 완료하지 못했습니다. 선택 내용은 유지되니 잠시 후 다시 시도해 주세요.`,
        SESSION_PLATFORM_MISMATCH:
          "다른 쇼핑몰 플랫폼에서 만든 연결 정보입니다. 현재 사이트에서 다시 연결해 주세요.",
      } as Record<string, string>
    )[code] ?? "요청을 완료하지 못했습니다. 잠시 후 다시 시도해 주세요."
  );
}

if (commercePlatform === "cafe24") loadSharedFooter(commercePlatform);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
